/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-useless-escape */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import jsPDF from "jspdf";
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  AlignmentType,
  Table as DocxTable,
  TableCell as DocxTableCell,
  TableRow as DocxTableRow,
  WidthType,
  ExternalHyperlink,
  Footer,
  PageNumber,
  TabStopType,
  TabStopPosition,
} from "docx";
import {
  Box,
  Typography,
  Button,
  IconButton,
  styled,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Alert,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Menu,
  Checkbox,
  Select,
  Pagination,
} from "@mui/material";
import {
  continuousComplianceService,
  ContinuousComplianceResponse,
} from "@services/continuousCompliance";
import {
  affablebpmIntegrationService,
  ExternalGetRecordAndFilesDetailsResponse,
  ExternalGetFileByFileIdResponse,
} from "@services/affablebpmIntegration";
import { useAffableConnect } from "@hooks/useAffableConnect";
import AffableConnectedIcon from "./AffableConnectedIcon";
import { GlobalAdminLlmPromptButton } from "./GlobalAdminLlmPromptButton";
import { toast } from "react-toastify";
import { getSelectedCountryCode } from "@utils/countryUtils";
import { getSelectedFacilityFromSession } from "@utils/facilityUtils";
import { auditLogService } from "@services/auditLog";
import { getCurrentUserId } from "@utils/userUtils";
import { DeleteFileIcon } from "@icons/DeleteFileIcon";
function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"] as const;
  let size = bytes;
  let unitIndex = 0;
  while (size >= 1024 && unitIndex < units.length - 1) {
    size /= 1024;
    unitIndex += 1;
  }
  const precision = unitIndex === 0 ? 0 : 1;
  return `${size.toFixed(precision)} ${units[unitIndex]}`;
}

/** When the user selects a file — date and time shown on the active upload row */
function formatClientUploadTimestamp(date: Date = new Date()): string {
  return `${date.toLocaleDateString("en-US", {
    month: "short",
    day: "2-digit",
    year: "numeric",
  })}, ${date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  })}`;
}

/** Document outline — same glyph as Recently Reviewed rows */
function ComplianceHistoryFileGlyph(): React.ReactElement {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6 5C6 3.89543 6.89543 3 8 3H16V10C16 11.6569 17.3431 13 19 13H26V27C26 28.1046 25.1046 29 24 29H8C6.89543 29 6 28.1046 6 27V5ZM25.927 12H19C17.8954 12 17 11.1046 17 10V3.07303C17.3293 3.16456 17.6327 3.33984 17.8787 3.58579L25.4142 11.1213C25.6602 11.3673 25.8354 11.6707 25.927 12ZM8 2C6.34315 2 5 3.34315 5 5V27C5 28.6569 6.34315 30 8 30H24C25.6569 30 27 28.6569 27 27V12.5355C27 11.7399 26.6839 10.9768 26.1213 10.4142L18.5858 2.87868C18.0232 2.31607 17.2601 2 16.4645 2H8Z"
        fill="#262626"
      />
    </svg>
  );
}

async function generateStyledCompliancePDF(
  analysis: ContinuousComplianceResponse,
  documentType: "policy" | "emergency" | "survey",
  options?: { output?: "download" | "blob" },
): Promise<void | Blob> {
  const pdf = new jsPDF();

  // Basic page formatting defaults
  pdf.setFont("helvetica", "normal");
  pdf.setTextColor(44, 62, 80);
  pdf.setLineHeightFactor(1.25);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 25;
  let yPosition = margin;

  const darkBlue = [52, 73, 94];
  const textColor = [44, 62, 80];
  const teal = [6, 91, 112];
  const tealBorder = [153, 212, 225];

  const footerTitleMap: Record<typeof documentType, string> = {
    policy: "UniComply.ai - Policy Compliance Report",
    emergency: "UniComply.ai - Emergency Plan Compliance Report",
    survey: "UniComply.ai - Survey Compliance Report",
  } as const;
  const footerTitle = footerTitleMap[documentType];
  const generatedDate = new Date().toLocaleDateString();

  const checkNewPage = (currentY: number, requiredSpace: number) => {
    if (currentY + requiredSpace > pageHeight - margin) {
      pdf.addPage();
      return margin;
    }
    return currentY;
  };

  // Load logo as data URL to avoid path/CORS issues
  const loadLogoDataUrl = async (): Promise<string | null> => {
    const candidates: string[] = [];
    try {
      const origin = (window as any)?.location?.origin || "";
      // Root-absolute paths only — PUBLIC_URL=. or "/" must not yield "./…" or "//…".
      candidates.push(`${origin}/unicomply.png`);
      candidates.push(`/unicomply.png`);
      candidates.push(`${origin}/logo512.png`);
    } catch {
      candidates.push(`/unicomply.png`);
    }

    for (const url of candidates) {
      try {
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) continue;
        const blob = await res.blob();
        const reader = new FileReader();
        const dataUrl: string = await new Promise((resolve, reject) => {
          reader.onloadend = () => resolve(String(reader.result || ""));
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        if (dataUrl) return dataUrl;
      } catch {}
    }
    return null;
  };

  const logoDataUrl = await loadLogoDataUrl();

  const addMedicalHeader = () => {
    // Title on left, logo on right, no solid band (match Figma)
    const teal = [6, 91, 112]; // #065b70

    // Logo on right + wordmark text
    if (logoDataUrl) {
      try {
        const logoWidth = 10;
        const logoHeight = 10;
        // Nudge the circle icon slightly further down so text centers vertically
        const logoY = 10;
        // Measure wordmark so we can ensure right padding and prevent clipping
        const textFontSize = 12; // increase logo text size
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(textFontSize);
        const uniWidth =
          (pdf.getStringUnitWidth("Uni") * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        pdf.setFont("helvetica", "normal");
        const complyWidth =
          (pdf.getStringUnitWidth("Comply.ai") * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        const wordWidth = uniWidth + complyWidth;
        const rightPadding = 4; // nudge further right
        const gapBetween = 2; // reduced spacing between icon and text
        // Shift logo left enough so full wordmark fits on the right
        const logoX = Math.max(
          margin,
          pageWidth -
            margin -
            logoWidth -
            wordWidth -
            gapBetween -
            rightPadding,
        );
        pdf.addImage(logoDataUrl, "PNG", logoX, logoY, logoWidth, logoHeight);
        // Wordmark next to logo: Uni (teal) + Comply.ai (gray)
        // Vertically center the wordmark text with the circle icon
        // Use an ascent correction so the baseline aligns to optical center
        const ascentCorrection = textFontSize * 0.18; // slight upward shift (center better)
        const wordY = logoY + logoHeight / 2 + ascentCorrection;
        // Place text to the RIGHT of the round logo with smaller gap
        let textX = logoX + logoWidth + gapBetween;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(textFontSize);
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        pdf.text("Uni", textX, wordY);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(102, 102, 102);
        pdf.text("Comply.ai", textX + uniWidth, wordY);
      } catch {
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(12);
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        pdf.text("UniComply.ai", pageWidth - margin - 60, 20);
      }
    } else {
      pdf.setFont("helvetica", "bold");
      pdf.setFontSize(12);
      pdf.setTextColor(teal[0], teal[1], teal[2]);
      pdf.text("UniComply.ai", pageWidth - margin - 60, 20);
    }

    // Title and subtitle on left
    // Dynamic report title based on which report is being viewed/downloaded
    const titleMap: Record<typeof documentType, string> = {
      policy: "POLICY & PROCEDURE REVIEW",
      emergency: "EMERGENCY PLAN REVIEW",
      survey: "SURVEY REVIEW",
    } as const;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(18);
    pdf.setTextColor(teal[0], teal[1], teal[2]);
    // Align title baseline roughly with wordmark baseline for visual alignment
    const titleBaselineY = 18; // default
    pdf.text(titleMap[documentType], margin, titleBaselineY);

    // Subheader style (lighter teal, smaller size)
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9.5);
    pdf.setTextColor(90, 136, 146); // softer teal for subtitle
    pdf.text("Healthcare Facility Risk Assessment", margin, 26);

    // Divider line
    pdf.setDrawColor(206, 228, 232);
    pdf.setLineWidth(0.6);
    pdf.line(margin, 32, pageWidth - margin, 32);

    return 40;
  };

  const addReportMetadata = (yPos: number) => {
    const cardW = pageWidth - 2 * margin;
    const cardH = 40; // increase height to create larger gaps
    pdf.setDrawColor(tealBorder[0], tealBorder[1], tealBorder[2]);
    pdf.setLineWidth(0.6);
    pdf.roundedRect(margin, yPos, cardW, cardH, 4, 4);

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(teal[0], teal[1], teal[2]);

    const meta: any = (analysis as any)?.metadata;
    const fileName = meta?.filename || "Unknown File";
    const reportDate = new Date().toLocaleDateString();

    const row1Y = yPos + 11;
    const row2Y = yPos + 22; // larger gap after report date
    const row3Y = yPos + 33; // larger gap after document

    pdf.text("Report Date:", margin + 8, row1Y);
    pdf.text("Document:", margin + 8, row2Y);
    pdf.text("Analysis Type:", margin + 8, row3Y);

    const vX = margin + 55; // align values/chips to a consistent column
    const maxValueWidth = cardW - (vX - margin) - 12; // inner right padding
    addPillValue(vX, row1Y, reportDate, maxValueWidth);
    addPillValue(vX, row2Y, fileName, maxValueWidth);
    addPillValue(
      vX,
      row3Y,
      "Policy & Procedure Compliance Analysis",
      maxValueWidth,
    );

    return yPos + cardH + 10;
  };

  const addSectionHeader = (title: string, yPos: number) => {
    yPos = checkNewPage(yPos, 16);

    // Divider line before title (Figma style)
    // Keep approx. one-row gap before divider to match spacing after content
    const dividerY = Math.max(margin, yPos - 2);
    pdf.setDrawColor(206, 228, 232);
    pdf.setLineWidth(0.6);
    pdf.line(margin, dividerY, pageWidth - margin, dividerY);

    // Title text (no background)
    pdf.setFontSize(12);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(teal[0], teal[1], teal[2]); // #065B70
    pdf.text(String(title).toUpperCase(), margin + 5, yPos + 6);

    return yPos + 18;
  };

  const addPillValue = (
    x: number,
    baselineY: number,
    value: string,
    maxWidth?: number,
  ) => {
    pdf.setFontSize(10);
    pdf.setFont("helvetica", "normal");
    const padX = 4;
    const boxH = 8;
    const textWidth = (t: string) =>
      pdf.getStringUnitWidth(t) *
      (pdf.getFontSize() / pdf.internal.scaleFactor);
    const ellipsis = "…";
    let textVal = String(value);
    if (maxWidth && textWidth(textVal) + padX * 2 + 2 > maxWidth) {
      // binary search to truncate
      let lo = 0,
        hi = textVal.length;
      while (lo < hi) {
        const mid = Math.floor((lo + hi) / 2);
        const candidate = textVal.slice(0, mid) + ellipsis;
        if (textWidth(candidate) + padX * 2 + 2 <= maxWidth) lo = mid + 1;
        else hi = mid;
      }
      textVal = textVal.slice(0, Math.max(0, lo - 1)) + ellipsis;
    }
    const textW = textWidth(textVal);
    const boxW = Math.min(
      maxWidth || textW + padX * 2 + 2,
      textW + padX * 2 + 2,
    );
    // Lift the gray chip to better vertically center around the text baseline
    const boxY = baselineY - boxH + 3;
    // Background-only chip with darker gray #F0F0F0 (no border)
    pdf.setFillColor(240, 240, 240);
    pdf.roundedRect(x - 2, boxY, boxW, boxH, 2, 2, "F");
    pdf.setTextColor(teal[0], teal[1], teal[2]);
    // center horizontally within chip
    const centeredX = x - 2 + (boxW - textW) / 2;
    pdf.text(textVal, centeredX, baselineY);
  };

  const addFieldRow = (
    label: string,
    value: any,
    yPos: number,
    indent = 0,
    isLink: boolean = false,
  ) => {
    yPos = checkNewPage(yPos, 8);

    pdf.setFontSize(10);
    pdf.setFont("helvetica", "bold");
    // Figma: labels in teal
    pdf.setTextColor(teal[0], teal[1], teal[2]);

    const labelX = margin + 5 + indent;
    const labelText = String(label);
    pdf.text(labelText + ":", labelX, yPos);

    // Compute dynamic start X for value to avoid overlapping long labels
    const labelWidth =
      (pdf.getStringUnitWidth(labelText + ":") * pdf.getFontSize()) /
      pdf.internal.scaleFactor;
    const minValueX = margin + 50 + indent; // align all values to the same column
    const valueX = Math.max(minValueX, labelX + labelWidth + 6);

    if (value !== undefined && value !== null && String(value).trim() !== "") {
      pdf.setFont("helvetica", "normal");
      const cleanValue = String(value).replace(/[^\x20-\x7E\n]/g, "");
      const maxWidth = pageWidth - valueX - (margin - 5);
      const lines = pdf.splitTextToSize(cleanValue, Math.max(40, maxWidth));
      let currentY = yPos;
      if (isLink && typeof value === "string") {
        // Render as blue, clickable hyperlink (first line clickable)
        pdf.setTextColor(33, 150, 243);
        lines.forEach((line: string, index: number) => {
          currentY = checkNewPage(currentY, 8);
          if (index === 0) {
            pdf.textWithLink(line, valueX, currentY, { url: value as string });
          } else {
            pdf.text(line, valueX, currentY);
          }
          currentY += 5;
        });
        return currentY + 3;
      }
      // Figma style: value text in teal
      pdf.setTextColor(teal[0], teal[1], teal[2]);
      lines.forEach((line: string) => {
        currentY = checkNewPage(currentY, 8);
        pdf.text(line, valueX, currentY);
        currentY += 5;
      });
      return currentY + 3;
    }

    return yPos + 8;
  };

  const addTable = (
    headers: string[],
    rows: string[][],
    yPos: number,
    columnWidths?: number[],
  ) => {
    const tableWidth = pageWidth - 2 * margin;
    const numColumns = headers.length;

    // Calculate column widths if not provided
    const colWidths =
      columnWidths || Array(numColumns).fill(tableWidth / numColumns);

    const rowHeight = 8;
    const headerHeight = 10;
    const cellPadding = 3;

    // Check if we need a new page for the header
    yPos = checkNewPage(yPos, headerHeight + 10);

    // Draw header row
    pdf.setFillColor(10, 130, 157); // #0A829D
    pdf.rect(margin, yPos, tableWidth, headerHeight, "F");

    pdf.setFontSize(9);
    pdf.setFont("helvetica", "bold");
    pdf.setTextColor(255, 255, 255);

    let xPos = margin;
    headers.forEach((header, i) => {
      const text = pdf.splitTextToSize(header, colWidths[i] - cellPadding * 2);
      pdf.text(text[0], xPos + cellPadding, yPos + 6);
      xPos += colWidths[i];
    });

    yPos += headerHeight;

    // Draw data rows
    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(8);

    rows.forEach((row, rowIndex) => {
      // Calculate row height based on content
      let maxLines = 1;
      row.forEach((cell, i) => {
        const lines = pdf.splitTextToSize(
          String(cell || "N/A"),
          colWidths[i] - cellPadding * 2,
        );
        maxLines = Math.max(maxLines, lines.length);
      });

      const currentRowHeight = Math.max(
        rowHeight,
        maxLines * 4 + cellPadding * 2,
      );

      // Check if we need a new page
      yPos = checkNewPage(yPos, currentRowHeight + 2);

      // Alternating row colors
      if (rowIndex % 2 === 0) {
        pdf.setFillColor(250, 250, 250);
        pdf.rect(margin, yPos, tableWidth, currentRowHeight, "F");
      }

      // Draw cell borders
      pdf.setDrawColor(229, 231, 235);
      pdf.setLineWidth(0.3);

      xPos = margin;
      row.forEach((cell, i) => {
        pdf.rect(xPos, yPos, colWidths[i], currentRowHeight);
        xPos += colWidths[i];
      });

      // Draw cell content
      xPos = margin;
      pdf.setTextColor(38, 38, 38); // #262626

      row.forEach((cell, i) => {
        const lines = pdf.splitTextToSize(
          String(cell || "N/A"),
          colWidths[i] - cellPadding * 2,
        );

        // Special color for compliance gap column (3rd column in mock survey matrix)
        if (i === 2 && headers[i] === "Compliance Gap") {
          pdf.setTextColor(164, 38, 44); // #A4262C
        } else {
          pdf.setTextColor(38, 38, 38);
        }

        lines.forEach((line: string, lineIndex: number) => {
          pdf.text(
            line,
            xPos + cellPadding,
            yPos + cellPadding + 3 + lineIndex * 4,
          );
        });

        xPos += colWidths[i];
      });

      yPos += currentRowHeight;
    });

    return yPos + 4;
  };

  // Header + metadata
  yPosition = addMedicalHeader();
  yPosition = addReportMetadata(yPosition);

  const data: any = (analysis as any)?.analysis || {};

  // Risk Score details (textual)
  if (data?.risk_score) {
    const rs = data.risk_score;
    yPosition += 4;
    yPosition = addSectionHeader("Risk Score", yPosition);
    yPosition = addFieldRow(
      "Overall Score",
      `${rs?.overall_score ?? "-"} (${rs?.risk_level ?? "N/A"})`,
      yPosition,
      5,
    );
    yPosition = addFieldRow(
      "Issues Count",
      rs?.issues_count ?? "-",
      yPosition,
      5,
    );
    yPosition = addFieldRow(
      "Actions Count",
      rs?.actions_count ?? "-",
      yPosition,
      5,
    );
    if (rs?.score_breakdown) {
      yPosition = addFieldRow("Score Breakdown", "", yPosition, 5);
      yPosition = addFieldRow(
        "Documentation",
        rs?.score_breakdown?.documentation ?? "-",
        yPosition,
        10,
      );
      yPosition = addFieldRow(
        "Compliance",
        rs?.score_breakdown?.compliance ?? "-",
        yPosition,
        10,
      );
      yPosition = addFieldRow(
        "Safety",
        rs?.score_breakdown?.safety ?? "-",
        yPosition,
        10,
      );
      yPosition = addFieldRow(
        "Financial Risk",
        rs?.score_breakdown?.financial_risk ?? "-",
        yPosition,
        10,
      );
    }
    if (rs?.justification) {
      // Render Justification as a key-value row like other fields
      yPosition = addFieldRow("Justification", rs.justification, yPosition, 5);
      // slightly smaller spacing before divider
      yPosition += 4;
      pdf.setDrawColor(206, 228, 232);
      pdf.setLineWidth(0.6);
      pdf.line(margin, yPosition, pageWidth - margin, yPosition);
      yPosition += 2;
    }
  }

  // Incident Summary (if present)
  if (data?.incident_summary) {
    yPosition = addSectionHeader("Incident Summary", yPosition);
    const inc = data.incident_summary;
    yPosition = addFieldRow("Narrative", inc?.narrative, yPosition);
    yPosition = addFieldRow("Type", inc?.incident_type, yPosition);
    yPosition = addFieldRow(
      "Clinical Classification",
      inc?.clinical_classification,
      yPosition,
    );
    yPosition = addFieldRow("Date", inc?.date, yPosition);
    yPosition = addFieldRow("Location", inc?.location, yPosition);
    yPosition = addFieldRow("Description", inc?.description, yPosition);
    if (Array.isArray(inc?.parties_involved)) {
      const joined = inc.parties_involved
        .map((p: any) => `"${String(p)}"`)
        .join(",  ");
      yPosition = addFieldRow("Parties Involved", joined, yPosition);
    }
    yPosition = addFieldRow(
      "Injuries/Damages",
      inc?.injuries_damages,
      yPosition,
    );
    yPosition = addFieldRow(
      "Environmental Factors",
      inc?.environmental_factors,
      yPosition,
    );
    yPosition = addFieldRow(
      "Reporting Timeliness",
      inc?.reporting_timeliness,
      yPosition,
    );
    yPosition = addFieldRow(
      "Documentation Completeness",
      inc?.documentation_completeness,
      yPosition,
    );
  }

  // Compliance Deficiencies
  if (
    Array.isArray(data?.compliance_deficiencies) &&
    data.compliance_deficiencies.length > 0
  ) {
    yPosition = addSectionHeader("Compliance Deficiencies", yPosition);
    data.compliance_deficiencies.forEach((def: any, idx: number) => {
      yPosition = addFieldRow(
        `#${idx + 1} ${def?.category || "Category"}`,
        "",
        yPosition,
      );
      // Requirement label and red alert box
      if (def?.requirement) {
        // Label above box
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        yPosition = checkNewPage(yPosition, 10);
        pdf.text("Requirement:", margin + 5, yPosition);
        yPosition += 4;

        // Box with balanced inner padding
        const alertLines = pdf.splitTextToSize(
          String(def.requirement),
          pageWidth - 2 * margin - 14,
        );
        const lineH = 5;
        const padY = 3; // symmetric padding; adjust baseline for optical balance
        const alertH = alertLines.length * lineH + padY * 2;
        yPosition = checkNewPage(yPosition, alertH + 6);
        pdf.setFillColor(255, 235, 238);
        // Change border to #5BBFD6 and set 0.5px border width
        pdf.setDrawColor(91, 191, 214);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(
          margin + 5,
          yPosition,
          pageWidth - 2 * margin - 10,
          alertH,
          3,
          3,
          "FD",
        );
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(212, 52, 56);
        const baselineAdjust = -1; // nudge up 1px to balance top/bottom visually
        alertLines.forEach((l: string, i: number) => {
          const by = yPosition + padY + (i + 1) * lineH + baselineAdjust;
          pdf.text(l, margin + 9, by);
        });
        yPosition += alertH + 8; // increased spacing after box before Risk Level
      }
      // Move Risk Level below Requirement per layout
      yPosition = addFieldRow(
        "Risk Level",
        def?.risk_level || "N/A",
        yPosition,
        5,
      );
      yPosition = addFieldRow(
        "Source",
        def?.regulatory_source || "N/A",
        yPosition,
        5,
      );
      if (def?.specific_regulation_citation) {
        yPosition = addFieldRow(
          "Citation",
          def?.specific_regulation_citation,
          yPosition,
          5,
        );
      }
      if (def?.official_url) {
        yPosition = addFieldRow(
          "Official URL",
          def?.official_url,
          yPosition,
          5,
          true,
        );
      }
      yPosition = addFieldRow(
        "Deficiency",
        def?.deficiency_description,
        yPosition,
        5,
      );
      const risksArr = (def?.associated_risks || []) as string[];
      if (risksArr.length) {
        const list = risksArr.map((r: string) => `• ${r}`);
        const riskLines = pdf.splitTextToSize(
          list.join("\n"),
          pageWidth - 2 * margin - 14,
        );
        const panelH = riskLines.length * 5 + 12;
        yPosition = checkNewPage(yPosition, panelH + 6);
        pdf.setFillColor(236, 252, 255);
        pdf.setDrawColor(tealBorder[0], tealBorder[1], tealBorder[2]);
        pdf.roundedRect(
          margin + 5,
          yPosition,
          pageWidth - 2 * margin - 10,
          panelH,
          3,
          3,
          "FD",
        );
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        pdf.text("Associated Risks:", margin + 9, yPosition + 8);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(textColor[0], textColor[1], textColor[2]);
        riskLines.forEach((l: string, i: number) =>
          pdf.text(l, margin + 9, yPosition + 14 + i * 5),
        );
        yPosition += panelH + 4;
      }
      yPosition += 4;
    });
  }

  // Potential Fines & Penalties
  if (data?.potential_penalties) {
    yPosition = addSectionHeader("Potential Fines & Penalties", yPosition);
    (data.potential_penalties?.penalty_table || []).forEach((p: any) => {
      yPosition = addFieldRow("Regulator", p?.regulator ?? "N/A", yPosition, 5);
      yPosition = addFieldRow(
        "Fine Range",
        p?.fine_range ?? "N/A",
        yPosition,
        5,
      );
      yPosition = addFieldRow(
        "Estimated Exposure",
        p?.estimated_exposure ?? "N/A",
        yPosition,
        5,
      );
      if (p?.notes) yPosition = addFieldRow("Notes", p?.notes, yPosition, 5);
      yPosition += 2;
    });
    // Intentionally omit penalty_charts from styled PDF per requirement
  }

  // Plan of Correction
  if (data?.plan_of_correction) {
    yPosition = addSectionHeader("Plan of Correction (POC)", yPosition);
    (data.plan_of_correction?.action_table || []).forEach(
      (action: any, idx: number) => {
        yPosition = addFieldRow(`Action ${idx + 1}`, action?.action, yPosition);
        yPosition = addFieldRow(
          "Priority",
          normalizePriority(action?.priority),
          yPosition,
          5,
        );
        yPosition = addFieldRow("Timeline", action?.timeline, yPosition, 5);
        yPosition = addFieldRow(
          "Responsible",
          action?.responsible,
          yPosition,
          5,
        );
        yPosition += 2;
      },
    );
    (data.plan_of_correction?.training_requirements || []).forEach((t: any) => {
      yPosition = addFieldRow("Training", t?.training_topic, yPosition);
      yPosition = addFieldRow(
        "Target Audience",
        t?.target_audience,
        yPosition,
        5,
      );
      yPosition = addFieldRow("Frequency", t?.frequency, yPosition, 5);
      yPosition = addFieldRow("Duration", t?.duration, yPosition, 5);
      yPosition += 2;
    });

    // Documentation Improvements (within POC)
    const docImprovements = data.plan_of_correction?.documentation_improvements;
    if (Array.isArray(docImprovements) && docImprovements.length > 0) {
      yPosition = addFieldRow("Documentation Improvements", "", yPosition);
      docImprovements.forEach((doc: any, idx: number) => {
        yPosition = addFieldRow(
          `Improvement ${idx + 1}`,
          doc?.improvement,
          yPosition,
          5,
        );
        if (
          Array.isArray(doc?.implementation_steps) &&
          doc.implementation_steps.length
        ) {
          yPosition = addFieldRow(
            "Implementation Steps",
            doc.implementation_steps.join(", "),
            yPosition,
            10,
          );
        }
        if (doc?.timeline)
          yPosition = addFieldRow("Timeline", doc.timeline, yPosition, 10);
        yPosition += 2;
      });
    }

    // System Improvements (within POC)
    const systemImprovements = data.plan_of_correction?.system_improvements;
    if (Array.isArray(systemImprovements) && systemImprovements.length > 0) {
      yPosition = addFieldRow("System Improvements", "", yPosition);
      systemImprovements.forEach((sys: any, idx: number) => {
        yPosition = addFieldRow(`System ${idx + 1}`, sys?.system, yPosition, 5);
        yPosition = addFieldRow("Improvement", sys?.improvement, yPosition, 10);
        if (sys?.implementation_cost)
          yPosition = addFieldRow(
            "Implementation Cost",
            sys.implementation_cost,
            yPosition,
            10,
          );
        if (sys?.timeline)
          yPosition = addFieldRow("Timeline", sys.timeline, yPosition, 10);
        yPosition += 2;
      });
    }

    // Monitoring & Tracking (within POC)
    const monitoringTracking = data.plan_of_correction?.monitoring_tracking;
    if (Array.isArray(monitoringTracking) && monitoringTracking.length > 0) {
      yPosition = addFieldRow("Monitoring & Tracking", "", yPosition);
      monitoringTracking.forEach((m: any, idx: number) => {
        yPosition = addFieldRow(`Metric ${idx + 1}`, m?.metric, yPosition, 5);
        if (m?.frequency)
          yPosition = addFieldRow("Frequency", m.frequency, yPosition, 10);
        if (m?.responsible_party)
          yPosition = addFieldRow(
            "Responsible",
            m.responsible_party,
            yPosition,
            10,
          );
        if (m?.reporting_method)
          yPosition = addFieldRow(
            "Reporting Method",
            m.reporting_method,
            yPosition,
            10,
          );
        yPosition += 2;
      });
    }

    const costs = data.plan_of_correction?.corrective_costs;
    if (costs) {
      yPosition = addFieldRow(
        "Estimated Corrective Costs",
        `Immediate: ${costs?.immediate_costs ?? "N/A"} | Long-term: ${
          costs?.long_term_costs ?? "N/A"
        } | Total: ${costs?.total_estimated_cost ?? "N/A"}`,
        yPosition,
      );
    }
  }

  // Recommendations
  if (
    data?.recommendations?.recommendation_table ||
    extractRecommendationsFromData(data).length
  ) {
    yPosition = addSectionHeader("Recommendations", yPosition);
    const recs = extractRecommendationsFromData(data);
    recs.forEach((r: any, idx: number) => {
      yPosition = addFieldRow(
        `Recommendation ${idx + 1}`,
        r?.recommendation || r?.title,
        yPosition,
      );
      yPosition = addFieldRow(
        "Priority",
        normalizePriority(r?.priority),
        yPosition,
        5,
      );
      if (r?.category)
        yPosition = addFieldRow("Category", r?.category, yPosition, 5);
      if (r?.rationale)
        yPosition = addFieldRow("Rationale", r?.rationale, yPosition, 5);
      if (r?.estimated_cost || r?.cost)
        yPosition = addFieldRow(
          "Estimated Cost",
          r?.estimated_cost || r?.cost,
          yPosition,
          5,
        );
      if (r?.expected_outcome || r?.outcome)
        yPosition = addFieldRow(
          "Expected Outcome",
          r?.expected_outcome || r?.outcome,
          yPosition,
          5,
        );
      if (r?.implementation_timeline)
        yPosition = addFieldRow(
          "Timeline",
          r?.implementation_timeline,
          yPosition,
          5,
        );

      // Dynamic extra keys
      const knownKeys = new Set([
        "recommendation",
        "title",
        "priority",
        "category",
        "rationale",
        "estimated_cost",
        "cost",
        "expected_outcome",
        "outcome",
        "implementation_timeline",
      ]);
      Object.entries(r || {}).forEach(([k, v]) => {
        if (
          !knownKeys.has(k) &&
          v !== undefined &&
          v !== null &&
          String(v) !== ""
        ) {
          yPosition = addFieldRow(
            prettifyLabel(k),
            stringifyValue(v),
            yPosition,
            5,
          );
        }
      });
      yPosition += 2;
    });
  }

  // Core Regulatory References
  if (
    Array.isArray(data?.core_regulatory_references) &&
    data.core_regulatory_references.length > 0
  ) {
    yPosition = addSectionHeader("Core Regulatory References", yPosition);
    data.core_regulatory_references.forEach((ref: any, idx: number) => {
      yPosition = addFieldRow(
        `Reference ${idx + 1}`,
        ref?.regulatory_source_title || "Reference",
        yPosition,
      );
      if (ref?.reference_url_link) {
        yPosition = addFieldRow(
          "URL",
          ref.reference_url_link,
          yPosition,
          5,
          true,
        );
      }
      if (ref?.key_relevance) {
        yPosition = addFieldRow(
          "Key Relevance",
          ref.key_relevance,
          yPosition,
          5,
        );
      }
      yPosition += 2;
    });
  }

  // Mock Survey Readiness Matrix
  if (
    Array.isArray(data?.mock_survey_readiness_matrix) &&
    data.mock_survey_readiness_matrix.length > 0
  ) {
    yPosition = addSectionHeader("Mock Survey Readiness Matrix", yPosition);

    const headers = [
      "Category",
      "Original Coverage",
      "Compliance Gap",
      "Corrective Action",
    ];
    const rows = data.mock_survey_readiness_matrix.map((item: any) => [
      item?.category || "N/A",
      item?.original_document_year_coverage || "N/A",
      item?.current_year_compliance_gap || "N/A",
      item?.corrective_action_to_update || "N/A",
    ]);

    // Custom column widths for better readability
    const tableWidth = pageWidth - 2 * margin;
    const columnWidths = [
      tableWidth * 0.2, // Category
      tableWidth * 0.25, // Original Coverage
      tableWidth * 0.25, // Compliance Gap
      tableWidth * 0.3, // Corrective Action
    ];

    yPosition = addTable(headers, rows, yPosition, columnWidths);
  }

  // Risk & Deficiency Exposure
  if (
    Array.isArray(data?.risk_deficiency_exposure) &&
    data.risk_deficiency_exposure.length > 0
  ) {
    yPosition = addSectionHeader("Risk & Deficiency Exposure", yPosition);
    data.risk_deficiency_exposure.forEach((item: any, idx: number) => {
      yPosition = addFieldRow(
        `Risk Area ${idx + 1}`,
        item?.risk_area || "Risk Area",
        yPosition,
      );
      if (item?.potential_deficiency) {
        // Render potential deficiency in a styled box
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        yPosition = checkNewPage(yPosition, 10);
        pdf.text("Potential Deficiency:", margin + 10, yPosition);
        yPosition += 4;

        const deficiencyLines = pdf.splitTextToSize(
          String(item.potential_deficiency),
          pageWidth - 2 * margin - 14,
        );
        const lineH = 5;
        const padY = 3;
        const boxH = deficiencyLines.length * lineH + padY * 2;
        yPosition = checkNewPage(yPosition, boxH + 6);
        pdf.setFillColor(255, 235, 238);
        pdf.setDrawColor(91, 191, 214);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(
          margin + 10,
          yPosition,
          pageWidth - 2 * margin - 20,
          boxH,
          3,
          3,
          "FD",
        );
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(212, 52, 56);
        deficiencyLines.forEach((l: string, i: number) => {
          const by = yPosition + padY + (i + 1) * lineH - 1;
          pdf.text(l, margin + 14, by);
        });
        yPosition += boxH + 4;
      }
      if (item?.preventative_action) {
        // Render preventative action in a styled box
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.setTextColor(teal[0], teal[1], teal[2]);
        yPosition = checkNewPage(yPosition, 10);
        pdf.text("Preventative Action:", margin + 10, yPosition);
        yPosition += 4;

        const actionLines = pdf.splitTextToSize(
          String(item.preventative_action),
          pageWidth - 2 * margin - 14,
        );
        const lineH = 5;
        const padY = 3;
        const boxH = actionLines.length * lineH + padY * 2;
        yPosition = checkNewPage(yPosition, boxH + 6);
        pdf.setFillColor(223, 246, 221);
        pdf.setDrawColor(91, 191, 214);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(
          margin + 10,
          yPosition,
          pageWidth - 2 * margin - 20,
          boxH,
          3,
          3,
          "FD",
        );
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(6, 91, 112);
        actionLines.forEach((l: string, i: number) => {
          const by = yPosition + padY + (i + 1) * lineH - 1;
          pdf.text(l, margin + 14, by);
        });
        yPosition += boxH + 4;
      }
      yPosition += 2;
    });
  }

  // State / Emirate Specific Requirements
  if (
    Array.isArray(data?.state_specific_requirements) &&
    data.state_specific_requirements.length > 0
  ) {
    yPosition = addSectionHeader(
      "State Specific Requirements",
      yPosition,
    );
    data.state_specific_requirements.forEach((req: any, idx: number) => {
      yPosition = addFieldRow(
        `Requirement ${idx + 1}`,
        req?.topic || "No topic specified",
        yPosition,
      );
      if (req?.state_reference_url) {
        yPosition = addFieldRow(
          "State Reference URL",
          req.state_reference_url,
          yPosition,
          5,
          true,
        );
      }
      if (req?.action) {
        yPosition = addFieldRow("Action", req.action, yPosition, 5);
      }
      if (req?.state_regulation_url) {
        yPosition = addFieldRow(
          "Regulation URL",
          req.state_regulation_url,
          yPosition,
          5,
          true,
        );
      }
      yPosition += 2;
    });
  }

  // Survey Readiness Checklist
  if (
    Array.isArray(data?.survey_readiness_checklist) &&
    data.survey_readiness_checklist.length > 0
  ) {
    yPosition = addSectionHeader("Survey Readiness Checklist", yPosition);
    data.survey_readiness_checklist.forEach((item: any, idx: number) => {
      yPosition = addFieldRow(
        `Checklist Item ${idx + 1}`,
        item?.item || "No item specified",
        yPosition,
      );
      if (item?.status) {
        yPosition = addFieldRow("Status", item.status, yPosition, 5);
      }
      if (item?.notes) {
        yPosition = addFieldRow("Notes", item.notes, yPosition, 5);
      }
      yPosition += 2;
    });
  }

  // Suggested Addenda
  if (
    Array.isArray(data?.suggested_addenda) &&
    data.suggested_addenda.length > 0
  ) {
    yPosition = addSectionHeader("Suggested Addenda", yPosition);
    data.suggested_addenda.forEach((addendum: any, idx: number) => {
      yPosition = addFieldRow(
        `Addendum ${idx + 1}`,
        addendum?.section || "No section specified",
        yPosition,
      );
      if (addendum?.addendum_text) {
        yPosition = addFieldRow(
          "Suggested Text",
          addendum.addendum_text,
          yPosition,
          5,
        );
      }
      if (addendum?.regulation_link) {
        yPosition = addFieldRow(
          "Regulation Reference",
          addendum.regulation_link,
          yPosition,
          5,
          true,
        );
      }
      if (addendum?.implementation_example) {
        yPosition = addFieldRow(
          "Implementation Example",
          addendum.implementation_example,
          yPosition,
          5,
        );
      }
      yPosition += 2;
    });
  }

  // Risk Ratings Summary
  if (
    Array.isArray(data?.risk_ratings_summary) &&
    data.risk_ratings_summary.length > 0
  ) {
    yPosition = addSectionHeader("Risk Ratings Summary", yPosition);
    data.risk_ratings_summary.forEach((risk: any, idx: number) => {
      yPosition = addFieldRow(
        `Risk Category ${idx + 1}`,
        risk?.category || "No category specified",
        yPosition,
      );
      if (risk?.compliance_risk) {
        yPosition = addFieldRow(
          "Compliance Risk",
          risk.compliance_risk,
          yPosition,
          5,
        );
      }
      if (
        risk?.readiness_score !== undefined &&
        risk?.readiness_score !== null
      ) {
        const riskScore =
          risk?.risk_score ?? 100 - Number(risk.readiness_score);
        yPosition = addFieldRow(
          "Risk Score",
          `${riskScore} / 100`,
          yPosition,
          5,
        );
      }
      if (
        Array.isArray(risk?.document_sections_analyzed) &&
        risk.document_sections_analyzed.length > 0
      ) {
        yPosition = addFieldRow(
          "Document Sections Analyzed",
          risk.document_sections_analyzed.join(", "),
          yPosition,
          5,
        );
      }
      if (
        Array.isArray(risk?.regulatory_sections_added) &&
        risk.regulatory_sections_added.length > 0
      ) {
        yPosition = addFieldRow(
          "Missing Regulatory Requirements",
          risk.regulatory_sections_added.join(", "),
          yPosition,
          5,
        );
      }
      if (risk?.analysis_basis) {
        yPosition = addFieldRow(
          "Analysis Basis",
          risk.analysis_basis,
          yPosition,
          5,
        );
      }
      yPosition += 2;
    });
  }

  // Final Recommendations
  if (
    Array.isArray(data?.final_recommendations) &&
    data.final_recommendations.length > 0
  ) {
    yPosition = addSectionHeader("Final Recommendations", yPosition);
    data.final_recommendations.forEach((rec: any, idx: number) => {
      yPosition = addFieldRow(
        `Recommendation ${idx + 1}`,
        rec?.recommendation || rec?.action || "N/A",
        yPosition,
      );
      if (rec?.priority) {
        yPosition = addFieldRow(
          "Priority",
          normalizePriority(rec.priority),
          yPosition,
          5,
        );
      }
      if (rec?.category) {
        yPosition = addFieldRow("Category", rec.category, yPosition, 5);
      }
      if (rec?.rationale) {
        yPosition = addFieldRow("Rationale", rec.rationale, yPosition, 5);
      }
      if (rec?.timeline) {
        yPosition = addFieldRow("Timeline", rec.timeline, yPosition, 5);
      }
      if (rec?.responsible) {
        yPosition = addFieldRow("Responsible", rec.responsible, yPosition, 5);
      }
      if (rec?.estimated_cost) {
        yPosition = addFieldRow(
          "Estimated Cost",
          rec.estimated_cost,
          yPosition,
          5,
        );
      }
      if (rec?.expected_outcome) {
        yPosition = addFieldRow(
          "Expected Outcome",
          rec.expected_outcome,
          yPosition,
          5,
        );
      }
      yPosition += 2;
    });
  }

  // Add footer to each page
  const totalPages = (pdf as any).getNumberOfPages
    ? (pdf as any).getNumberOfPages()
    : (pdf as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i += 1) {
    pdf.setPage(i);

    // Divider line
    pdf.setDrawColor(220, 227, 232);
    pdf.setLineWidth(0.5);
    pdf.line(
      margin - 5,
      pageHeight - 20,
      pageWidth - (margin - 5),
      pageHeight - 20,
    );

    // Footer branding (logo + wordmark on left)
    pdf.setFontSize(9);
    pdf.setTextColor(darkBlue[0], darkBlue[1], darkBlue[2]);
    pdf.setFont("helvetica", "normal");
    try {
      if (logoDataUrl) {
        const iconW = 8;
        const iconH = 8;
        const iconX = margin - 5;
        const baseY = pageHeight - 14; // text baseline
        // Position icon so its vertical center aligns with text center
        const iconY = baseY - iconH / 2 - 1;
        pdf.addImage(logoDataUrl, "PNG", iconX, iconY, iconW, iconH);
        const textFontSize = 9;
        const ascentCorrection = textFontSize * 0.18;
        const wordY = iconY + iconH / 2 + ascentCorrection;
        const textX = iconX + iconW + 2;
        pdf.setFont("helvetica", "bold");
        pdf.setFontSize(textFontSize);
        pdf.setTextColor(6, 91, 112);
        pdf.text("Uni", textX, wordY);
        const uniW =
          (pdf.getStringUnitWidth("Uni") * pdf.getFontSize()) /
          pdf.internal.scaleFactor;
        pdf.setFont("helvetica", "normal");
        pdf.setTextColor(102, 102, 102);
        pdf.text("Comply.ai", textX + uniW, wordY);
        // Report type after brand
        const reportLabel =
          documentType === "policy"
            ? " - Policy Compliance Report"
            : documentType === "emergency"
              ? " - Emergency Plan Compliance Report"
              : " - Survey Compliance Report";
        const brandWidth =
          ((pdf.getStringUnitWidth("Uni") +
            pdf.getStringUnitWidth("Comply.ai")) *
            pdf.getFontSize()) /
            pdf.internal.scaleFactor +
          2;
        pdf.setTextColor(darkBlue[0], darkBlue[1], darkBlue[2]);
        pdf.text(reportLabel, textX + brandWidth, wordY);
      } else {
        pdf.text(footerTitle, margin - 5, pageHeight - 14);
      }
    } catch {
      pdf.text(footerTitle, margin - 5, pageHeight - 14);
    }

    // Center: Page X of N
    const pageLabel = `Page ${i} of ${totalPages}`;
    const textWidth =
      (pdf.getStringUnitWidth(pageLabel) * pdf.getFontSize()) /
      pdf.internal.scaleFactor;
    pdf.text(pageLabel, pageWidth / 2 - textWidth / 2, pageHeight - 14);

    // Right: Generated date
    const genLabel = `Generated: ${generatedDate}`;
    const genWidth =
      (pdf.getStringUnitWidth(genLabel) * pdf.getFontSize()) /
      pdf.internal.scaleFactor;
    pdf.text(genLabel, pageWidth - (margin - 5) - genWidth, pageHeight - 14);

    // Confidential note
    pdf.setFontSize(7);
    pdf.setTextColor(128, 128, 128);
    pdf.text(
      "Generated using AI. Subject to human verification.",
      margin - 5,
      pageHeight - 8,
    );
  }

  const meta: any = (analysis as any)?.metadata;
  let baseName = (meta?.filename as string) || "compliance_report";
  baseName = baseName.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "_");
  const fileName = `${baseName}_${new Date().toISOString().split("T")[0]}.pdf`;

  if (options?.output === "blob") {
    // Return the PDF as a Blob for programmatic upload (no download)
    return pdf.output("blob");
  }

  // Default behavior: trigger browser download
  pdf.save(fileName);
}

const DownloadReport = (
  type: "policy" | "emergency" | "survey",
  analysis: ContinuousComplianceResponse,
) => {
  generateStyledCompliancePDF(analysis, type);
};

async function generateComplianceDocx(
  analysis: ContinuousComplianceResponse,
  documentType: "policy" | "emergency" | "survey",
) {
  const data: any = (analysis as any)?.analysis || {};
  const meta: any = (analysis as any)?.metadata;

  const titleMap: Record<typeof documentType, string> = {
    policy: "POLICY & PROCEDURE REVIEW",
    emergency: "EMERGENCY PLAN REVIEW",
    survey: "SURVEY REVIEW",
  };

  const sections: any[] = [];

  // Title
  sections.push(
    new Paragraph({
      text: titleMap[documentType],
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 },
    }),
    new Paragraph({
      text: "Healthcare Facility Risk Assessment",
      alignment: AlignmentType.CENTER,
      spacing: { after: 400 },
    }),
  );

  // Metadata
  sections.push(
    new Paragraph({
      text: "Report Information",
      heading: HeadingLevel.HEADING_2,
      spacing: { before: 200, after: 200 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: "Report Date: ", bold: true }),
        new TextRun(new Date().toLocaleDateString()),
      ],
      spacing: { after: 100 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: "Document: ", bold: true }),
        new TextRun(meta?.filename || "Unknown File"),
      ],
      spacing: { after: 100 },
    }),
    new Paragraph({
      children: [
        new TextRun({ text: "Analysis Type: ", bold: true }),
        new TextRun("Policy & Procedure Compliance Analysis"),
      ],
      spacing: { after: 300 },
    }),
  );

  // Risk Score
  if (data?.risk_score) {
    const rs = data.risk_score;
    sections.push(
      new Paragraph({
        text: "Risk Score",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 200 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Overall Score: ", bold: true }),
          new TextRun(
            `${rs?.overall_score ?? "-"} (${rs?.risk_level ?? "N/A"})`,
          ),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Issues Count: ", bold: true }),
          new TextRun(String(rs?.issues_count ?? "-")),
        ],
        spacing: { after: 100 },
      }),
      new Paragraph({
        children: [
          new TextRun({ text: "Actions Count: ", bold: true }),
          new TextRun(String(rs?.actions_count ?? "-")),
        ],
        spacing: { after: 300 },
      }),
    );
  }

  // Helper function to add sections
  const addSection = (
    title: string,
    items: any[],
    renderItem: (item: any, idx: number) => any[],
  ) => {
    if (Array.isArray(items) && items.length > 0) {
      sections.push(
        new Paragraph({
          text: title,
          heading: HeadingLevel.HEADING_2,
          spacing: { before: 200, after: 200 },
        }),
      );
      items.forEach((item, idx) => {
        sections.push(...renderItem(item, idx));
      });
    }
  };

  // Core Regulatory References
  addSection(
    "Core Regulatory References",
    data?.core_regulatory_references,
    (ref, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Reference ${idx + 1}: `, bold: true }),
          new TextRun(ref?.regulatory_source_title || "Reference"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(ref?.reference_url_link
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "URL: ", bold: true }),
                new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: ref.reference_url_link,
                      style: "Hyperlink",
                    }),
                  ],
                  link: ref.reference_url_link,
                }),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(ref?.key_relevance
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Key Relevance: ", bold: true }),
                new TextRun(ref.key_relevance),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // Mock Survey Readiness Matrix (as table)
  if (
    Array.isArray(data?.mock_survey_readiness_matrix) &&
    data.mock_survey_readiness_matrix.length > 0
  ) {
    sections.push(
      new Paragraph({
        text: "Mock Survey Readiness Matrix",
        heading: HeadingLevel.HEADING_2,
        spacing: { before: 200, after: 200 },
      }),
      new DocxTable({
        width: { size: 100, type: WidthType.PERCENTAGE },
        rows: [
          new DocxTableRow({
            children: [
              new DocxTableCell({
                children: [
                  new Paragraph({
                    children: [new TextRun({ text: "Category", bold: true })],
                  }),
                ],
              }),
              new DocxTableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Original Coverage", bold: true }),
                    ],
                  }),
                ],
              }),
              new DocxTableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Compliance Gap", bold: true }),
                    ],
                  }),
                ],
              }),
              new DocxTableCell({
                children: [
                  new Paragraph({
                    children: [
                      new TextRun({ text: "Corrective Action", bold: true }),
                    ],
                  }),
                ],
              }),
            ],
          }),
          ...data.mock_survey_readiness_matrix.map(
            (item: any) =>
              new DocxTableRow({
                children: [
                  new DocxTableCell({
                    children: [new Paragraph(item?.category || "N/A")],
                  }),
                  new DocxTableCell({
                    children: [
                      new Paragraph(
                        item?.original_document_year_coverage || "N/A",
                      ),
                    ],
                  }),
                  new DocxTableCell({
                    children: [
                      new Paragraph(item?.current_year_compliance_gap || "N/A"),
                    ],
                  }),
                  new DocxTableCell({
                    children: [
                      new Paragraph(item?.corrective_action_to_update || "N/A"),
                    ],
                  }),
                ],
              }),
          ),
        ],
      }),
    );
  }

  // Risk & Deficiency Exposure
  addSection(
    "Risk & Deficiency Exposure",
    data?.risk_deficiency_exposure,
    (item, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Risk Area ${idx + 1}: `, bold: true }),
          new TextRun(item?.risk_area || "N/A"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(item?.potential_deficiency
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Potential Deficiency: ", bold: true }),
                new TextRun(item.potential_deficiency),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(item?.preventative_action
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Preventative Action: ", bold: true }),
                new TextRun(item.preventative_action),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // State / Emirate Specific Requirements
  addSection(
    "State Specific Requirements",
    data?.state_specific_requirements,
    (req, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Requirement ${idx + 1}: `, bold: true }),
          new TextRun(req?.topic || "No topic specified"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(req?.state_reference_url
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "State Reference URL: ", bold: true }),
                new ExternalHyperlink({
                  children: [
                    new TextRun({
                      text: req.state_reference_url,
                      style: "Hyperlink",
                    }),
                  ],
                  link: req.state_reference_url,
                }),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(req?.action
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Action: ", bold: true }),
                new TextRun(req.action),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // Survey Readiness Checklist
  addSection(
    "Survey Readiness Checklist",
    data?.survey_readiness_checklist,
    (item, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Checklist Item ${idx + 1}: `, bold: true }),
          new TextRun(item?.item || "No item specified"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(item?.status
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Status: ", bold: true }),
                new TextRun(item.status),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(item?.notes
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Notes: ", bold: true }),
                new TextRun(item.notes),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // Suggested Addenda
  addSection("Suggested Addenda", data?.suggested_addenda, (addendum, idx) => [
    new Paragraph({
      children: [
        new TextRun({ text: `Addendum ${idx + 1}: `, bold: true }),
        new TextRun(addendum?.section || "No section specified"),
      ],
      spacing: { before: 100, after: 50 },
    }),
    ...(addendum?.addendum_text
      ? [
          new Paragraph({
            children: [
              new TextRun({ text: "Suggested Text: ", bold: true }),
              new TextRun(addendum.addendum_text),
            ],
            spacing: { after: 50 },
          }),
        ]
      : []),
    ...(addendum?.regulation_link
      ? [
          new Paragraph({
            children: [
              new TextRun({ text: "Regulation Reference: ", bold: true }),
              new ExternalHyperlink({
                children: [
                  new TextRun({
                    text: addendum.regulation_link,
                    style: "Hyperlink",
                  }),
                ],
                link: addendum.regulation_link,
              }),
            ],
            spacing: { after: 200 },
          }),
        ]
      : []),
  ]);

  // Risk Ratings Summary
  addSection(
    "Risk Ratings Summary",
    data?.risk_ratings_summary,
    (risk, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Risk Category ${idx + 1}: `, bold: true }),
          new TextRun(risk?.category || "No category specified"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(risk?.compliance_risk
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Compliance Risk: ", bold: true }),
                new TextRun(risk.compliance_risk),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(risk?.readiness_score !== undefined
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Risk Score: ", bold: true }),
                new TextRun(
                  `${risk.risk_score ?? 100 - Number(risk.readiness_score)} / 100`,
                ),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // Final Recommendations
  addSection(
    "Final Recommendations",
    data?.final_recommendations,
    (rec, idx) => [
      new Paragraph({
        children: [
          new TextRun({ text: `Recommendation ${idx + 1}: `, bold: true }),
          new TextRun(rec?.recommendation || rec?.action || "N/A"),
        ],
        spacing: { before: 100, after: 50 },
      }),
      ...(rec?.priority
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Priority: ", bold: true }),
                new TextRun(rec.priority),
              ],
              spacing: { after: 50 },
            }),
          ]
        : []),
      ...(rec?.category
        ? [
            new Paragraph({
              children: [
                new TextRun({ text: "Category: ", bold: true }),
                new TextRun(rec.category),
              ],
              spacing: { after: 200 },
            }),
          ]
        : []),
    ],
  );

  // Create footer with same info as PDF download
  const footerTitleMap: Record<typeof documentType, string> = {
    policy: "UniComply.ai - Policy Compliance Report",
    emergency: "UniComply.ai - Emergency Plan Compliance Report",
    survey: "UniComply.ai - Survey Compliance Report",
  };
  const footerText = footerTitleMap[documentType];
  const generatedDate = new Date().toLocaleDateString();

  const footer = new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        tabStops: [
          {
            type: TabStopType.LEFT,
            position: 0,
          },
          {
            type: TabStopType.CENTER,
            position: TabStopPosition.MAX / 2,
          },
          {
            type: TabStopType.RIGHT,
            position: TabStopPosition.MAX,
          },
        ],
        children: [
          new TextRun({
            text: footerText,
            size: 18,
            color: "065B70",
          }),
          new TextRun({
            text: "\t",
          }),
          new TextRun({
            text: "Page ",
            size: 18,
          }),
          new TextRun({
            children: [PageNumber.CURRENT],
            size: 18,
          }),
          new TextRun({
            text: " of ",
            size: 18,
          }),
          new TextRun({
            children: [PageNumber.TOTAL_PAGES],
            size: 18,
          }),
          new TextRun({
            text: "\t",
          }),
          new TextRun({
            text: `Generated: ${generatedDate}`,
            size: 18,
          }),
        ],
      }),
      new Paragraph({
        alignment: AlignmentType.LEFT,
        spacing: { before: 100 },
        children: [
          new TextRun({
            text: "Generated using AI. Subject to human verification.",
            size: 14,
            color: "808080",
          }),
        ],
      }),
    ],
  });

  const doc = new Document({
    sections: [
      {
        properties: {},
        children: sections,
        footers: {
          default: footer,
        },
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  let baseName = (meta?.filename as string) || "compliance_report";
  baseName = baseName.replace(/\.[^/.]+$/, "").replace(/[^a-zA-Z0-9_-]/g, "_");
  const fileName = `${baseName}_${new Date().toISOString().split("T")[0]}.docx`;

  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(link.href);
}

const DownloadDocxReport = (
  type: "policy" | "emergency" | "survey",
  analysis: ContinuousComplianceResponse,
) => {
  generateComplianceDocx(analysis, type);
};

const ContinuousComplianceContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  backgroundColor: "#ffffff",
  alignItems: "flex-start",
});

const AccordionsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  flex: "1",
  minWidth: "0",
  width: "100%",
  transition: "width 0.3s ease-in-out",
});

const ComplianceHeader = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "nowrap",
  gap: "8px",
  padding: "4px",
  background: "#fff",
  alignSelf: "stretch",
  width: "100%",
});

const ReportDetailsContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  maxWidth: "100%",
  padding: "16px",
  boxSizing: "border-box",
  backgroundColor: "#f9fafb",
  borderRadius: "8px",
  overflowX: "hidden",
});

const StyledAccordion = styled(Accordion)({
  width: "100%",
  boxSizing: "border-box",
  marginBottom: "8px",
  borderRadius: "8px !important",
  border: "1px solid #e0e0e0",
  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
  "&:before": {
    display: "none",
  },
  "&.Mui-expanded": {
    margin: "0 0 8px 0",
  },
  "& .accordion-title": {
    color: "#262626",
    minWidth: 0,
    width: "100%",
    transition: "color 0.3s ease",
  },
  "&.Mui-expanded .accordion-title": {
    color: "#065B70",
  },
  "& .MuiAccordionSummary-root": {
    alignItems: "center",
  },
  "& .MuiAccordionSummary-content": {
    alignItems: "center",
    display: "flex",
    flex: "1 1 auto",
    width: "100%",
    minWidth: 0,
    flexWrap: "nowrap",
    margin: "0",
  },
  "& .MuiAccordionDetails-root": { paddingTop: "0px" },
});

const AccordionTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  fontFamily: "Rethink Sans, sans-serif",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "12px",
  width: "100%",
  minWidth: 0,
  flex: "1 1 auto",
}) as typeof Typography;

const AccordionTitleLeading = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "8px",
  minWidth: 0,
  flex: "1 1 auto",
});

const AccordionCountBadge = styled(Box)({
  display: "flex",
  padding: "4px 16px",
  justifyContent: "center",
  alignItems: "center",
  gap: "4px",
  borderRadius: "16px",
  border: "1px solid #086E86",
  background: "#FFF",
  color: "#086E86",
  flexShrink: 0,
});

const AccordionIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "20px",
  height: "20px",
  flexShrink: 0,
});

const DownloadButton = styled(Button)({
  background: "linear-gradient(180deg, #0ea7c8 0%, #0a8faf 100%)",
  border: "1px solid #0b91b1",
  boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.25)",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  textTransform: "none",
  borderRadius: "8px",
  padding: "8px 16px",
  minHeight: "36px",
  lineHeight: 1,
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    background: "linear-gradient(180deg, #10b0d1 0%, #0b99ba 100%)",
    borderColor: "#0a8faf",
  },
  "&:active": {
    background: "linear-gradient(180deg, #0b96b5 0%, #087f9d 100%)",
  },
  "&.Mui-disabled": {
    color: "#ffffff",
    opacity: 0.6,
  },
});

const UploadActionButton = styled(Button)({
  backgroundColor: "#0e7490",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  borderRadius: "6px",
  padding: "6px 14px",
  "&:hover": { backgroundColor: "#0b5f75" },
  "&:disabled": { opacity: 0.6 },
});

const FileDisplayContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "12px",
  padding: "12px 16px",
  backgroundColor: "#f0f9ff",
  border: "1px solid #bae6fd",
  borderRadius: "8px",
  width: "calc(100% - 32px)",
});

const FileInfoContainer = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: "10px",
  flex: 1,
  minWidth: 0,
});

const FileIcon = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  flexShrink: 0,
  boxSizing: "border-box",
  backgroundColor: "#e6fafc",
  borderRadius: "8px",
  border: "1px solid #bae6fd",
});

const FileDetails = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  minWidth: 0,
  flex: 1,
});

const FileName = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#0f172a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const FileSize = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "11px",
  color: "#64748b",
  textTransform: "uppercase",
});

/** Icon-only: white tile + light gray border (reference mocks) */
const DeleteButton = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: "40px",
  height: "40px",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  cursor: "pointer",
  flexShrink: 0,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#f8fafc",
    borderColor: "#cbd5e1",
  },
});

/** Secondary outlined — height/padding aligned with View Analysis */
const ReUploadButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  minHeight: "40px",
  height: "40px",
  padding: "10px 20px",
  boxSizing: "border-box",
  borderRadius: "8px",
  border: "1px solid #5bbfd6",
  backgroundColor: "#e6fafc",
  cursor: "pointer",
  color: "#065b70",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  flexShrink: 0,
  lineHeight: 1,
  transition: "background-color 0.15s ease, border-color 0.15s ease",
  "&:hover": {
    backgroundColor: "#d1f5f8",
    borderColor: "#0e9aba",
  },
  "&:active": {
    backgroundColor: "#c1eff5",
  },
});

/** Primary — same gradient + shadow treatment as Potential Penalties Download XLS */
const ViewReportButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "8px",
  padding: "10px 20px",
  borderRadius: "8px",
  border: "none",
  cursor: "pointer",
  color: "#ffffff",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  textTransform: "none",
  flexShrink: 0,
  minHeight: "40px",
  height: "40px",
  lineHeight: 1,
  boxSizing: "border-box",
  background: "linear-gradient(90deg, #0A829D 0%, #58C2D8 100%)",
  boxShadow: "0 2px 8px 0 rgba(0, 0, 0, 0.25)",
  transition: "background 0.2s ease, box-shadow 0.2s ease, border 0.2s ease",
  "&:hover": {
    backgroundColor: "#065b70",
    boxShadow: "0px 4px 8px rgba(6, 91, 112, 0.3)",
    border: "1px solid #065b70",
  },
  "&:active": {
    backgroundColor: "#065b70",
    boxShadow: "0px 1px 2px rgba(6, 91, 112, 0.2)",
  },
});

const DescriptionText = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  color: "#475569",
  lineHeight: 1.6,
  margin: 0,
  fontWeight: 400,
  padding: "8px 12px",
});

const UploadContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "12px",
  width: "100%",
});

const UploadArea = styled(Box, {
  shouldForwardProp: (prop) => prop !== "$isDragOver",
})<{ $isDragOver?: boolean }>(({ $isDragOver }) => ({
  border: `1px dashed ${$isDragOver ? "#0e7490" : "#5bbfd6"}`,
  borderRadius: "8px",
  padding: "16px 8px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexGrow: 1,
  backgroundColor: $isDragOver ? "#f0fdff" : "transparent",
  transition: "all 0.2s ease-in-out",
  cursor: $isDragOver ? "copy" : "pointer",
  "&:hover": {
    backgroundColor: "#f0fdff",
    borderColor: "#0e7490",
  },
}));

const UploadButton = styled(Box)({
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  padding: "8px 16px",
  borderRadius: "8px",
  border: "1px solid #5bbfd6",
  backgroundColor: "#e6fafc",
  color: "#065B70",
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 500,
  cursor: "pointer",
  transition: "background-color 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: "#d1f5f8",
  },
  "&:active": {
    backgroundColor: "#c1eff5",
  },
});

const HiddenInput = styled("input")({
  display: "none",
});

const AffableFileRow = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: "2px",
  flex: 1,
  minWidth: 0,
});

const AffableFileName = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  color: "#0f172a",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

const AffableFileMeta = styled(Typography)({
  fontFamily: "Rethink Sans, sans-serif",
  fontSize: "11px",
  color: "#64748b",
});

function normalizePriority(priority: any): string {
  const value = String(priority || "").toUpperCase();
  if (["CRITICAL", "HIGH", "MEDIUM", "LOW"].includes(value)) return value;
  return value || "N/A";
}

function extractRecommendationsFromData(data: any): any[] {
  if (!data) return [];
  const table = (data?.recommendations as any)?.recommendation_table;
  if (Array.isArray(table)) return table;
  const direct = data?.recommendations;
  if (Array.isArray(direct)) return direct;
  const fallback = (data as any)?.analysis?.recommendations;
  if (Array.isArray(fallback)) return fallback;
  const items = (data?.recommendations as any)?.items;
  if (Array.isArray(items)) return items;
  return [];
}

function prettifyLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function stringifyValue(value: any): string {
  if (value == null) return "";
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  )
    return String(value);
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

interface RawAffableImage {
  ImageId?: number | string;
  ImageName?: string;
  ImageType?: string;
  ImageCreatedDate?: string;
}

interface RawAffableFolder {
  FolderId?: number | string;
  FolderName?: string;
  Images?: RawAffableImage[];
}

interface RawAffableDocument {
  DocumentId?: number | string;
  Folders?: RawAffableFolder[];
}

interface RawAffableDataItem {
  RecordList?: any[];
  RecordFileData?: RawAffableDocument[];
}

interface RawAffableResponse extends ExternalGetRecordAndFilesDetailsResponse {
  Status?: string;
  ErrorMsg?: string;
  Message?: string;
  Data?: RawAffableDataItem[];
  Record_TotalCount?: number | string;
}

interface AffableImageItem {
  id: number;
  name: string;
  type: string;
  createdAt: string;
}

interface AffableFolderItem {
  id: number;
  name: string;
  images: AffableImageItem[];
}

interface AffableDocumentItem {
  id: number;
  folders: AffableFolderItem[];
}

interface AffableRecordItem {
  DocumentID: number;
  DeptID: number;
  RowNum: number;
  [key: string]: any; // Allow any additional dynamic fields
}

function toNumericId(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

function safeStringValue(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function isNonNullable<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}

function normalizeAffableResponse(
  response: ExternalGetRecordAndFilesDetailsResponse | null | undefined,
): {
  documents: AffableDocumentItem[];
  records: AffableRecordItem[];
  totalCount: number;
} {
  const raw = (response || {}) as RawAffableResponse;
  // New API structure: Data is an array with one object containing RecordFileData and RecordList
  const dataItem =
    Array.isArray(raw?.Data) && raw.Data.length > 0 ? raw.Data[0] : null;
  const rawDocuments =
    dataItem?.RecordFileData && Array.isArray(dataItem.RecordFileData)
      ? dataItem.RecordFileData
      : [];
  const rawRecords =
    dataItem?.RecordList && Array.isArray(dataItem.RecordList)
      ? dataItem.RecordList
      : [];

  const documents: AffableDocumentItem[] = rawDocuments
    .map((doc) => {
      const documentId = toNumericId(doc?.DocumentId);
      if (documentId == null) return null;

      const folderList = Array.isArray(doc?.Folders) ? doc.Folders : [];
      const folders: AffableFolderItem[] = folderList
        .map((folder) => {
          const folderId = toNumericId(folder?.FolderId);
          if (folderId == null) return null;

          const imageList = Array.isArray(folder?.Images) ? folder.Images : [];
          const images: AffableImageItem[] = imageList
            .map((image) => {
              const imageId = toNumericId(image?.ImageId);
              if (imageId == null) return null;
              return {
                id: imageId,
                name: safeStringValue(image?.ImageName) || `File ${imageId}`,
                type: safeStringValue(image?.ImageType),
                createdAt: safeStringValue(image?.ImageCreatedDate),
              };
            })
            .filter(isNonNullable);

          const rawFolderName = safeStringValue(folder?.FolderName);
          const displayName =
            rawFolderName.toLowerCase() === "uncategorized"
              ? "Default"
              : rawFolderName || `Folder ${folderId}`;

          return {
            id: folderId,
            name: displayName,
            images,
          };
        })
        .filter(isNonNullable)
        .sort((a, b) => {
          const aIsDefault = a.name === "Default";
          const bIsDefault = b.name === "Default";
          if (aIsDefault && !bIsDefault) return -1;
          if (!aIsDefault && bIsDefault) return 1;
          return a.name.localeCompare(b.name);
        });

      return { id: documentId, folders };
    })
    .filter(isNonNullable);

  // Normalize RecordList data - preserve all fields dynamically
  const records: AffableRecordItem[] = rawRecords
    .map((record) => {
      const normalized: AffableRecordItem = {
        DocumentID: toNumericId(record?.DocumentID) ?? 0,
        DeptID: toNumericId(record?.DeptID) ?? 0,
        RowNum: toNumericId(record?.RowNum) ?? 0,
      };

      // Copy all other fields dynamically
      if (record && typeof record === "object") {
        Object.keys(record).forEach((key) => {
          if (key !== "DocumentID" && key !== "DeptID" && key !== "RowNum") {
            normalized[key] = record[key] ?? null;
          }
        });
      }

      return normalized;
    })
    .filter((r) => r.DocumentID > 0);

  const totalCount = toNumericId(raw?.Record_TotalCount) ?? documents.length;

  return { documents, records, totalCount };
}

interface ContinuousComplianceProps {
  facilityData?: any;
  /** True when the Continuous Compliance tab is visible. */
  complianceTabActive?: boolean;
  focusUploadSection?: "policy" | null;
  onFocusUploadSectionConsumed?: () => void;
  onWatchGuideClick?: () => void;
  watchGuideLoading?: boolean;
  watchGuideIconSrc?: string;
  showWatchGuide?: boolean;
}

export default function ContinuousCompliance({
  facilityData,
  complianceTabActive = false,
  focusUploadSection = null,
  onFocusUploadSectionConsumed,
  onWatchGuideClick,
  watchGuideLoading = false,
  watchGuideIconSrc,
  showWatchGuide = true,
}: ContinuousComplianceProps) {
  // Get facility data from prop or sessionStorage as fallback
  const getFacilityData = () => {
    let resolvedFacility = facilityData;
    if (!resolvedFacility) {
      resolvedFacility = getSelectedFacilityFromSession();
    }

    if (!resolvedFacility) {
      return null;
    }

    // Facility validation flow stores latest city/state separately; use them if missing.
    const fallbackCity = sessionStorage.getItem("sessionCity") || undefined;
    const fallbackState = sessionStorage.getItem("sessionState") || undefined;

    const normalizedCity =
      typeof resolvedFacility.city === "string" && resolvedFacility.city.trim()
        ? resolvedFacility.city
        : fallbackCity || resolvedFacility.city || "";

    const normalizedState =
      typeof resolvedFacility.state === "string" &&
      resolvedFacility.state.trim()
        ? resolvedFacility.state
        : fallbackState || resolvedFacility.state || "";

    return {
      ...resolvedFacility,
      city: normalizedCity,
      state: normalizedState,
    };
  };

  const {
    connectToAffableBPM: handleConnectAffableBPM,
    isConnectingAffable,
    getFacilityName,
  } = useAffableConnect({ getFacilityData });

  // Resolve facility once for render-time checks (e.g., Affable connection status)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const resolvedFacility = useMemo(() => getFacilityData(), [facilityData]);
  const isAffableConnected = Boolean(resolvedFacility?.is_affable_connected);

  // State for analysis history
  const [analysisHistory, setAnalysisHistory] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const analysisHistoryFetchKeyRef = useRef<string | null>(null);
  const [historyRefreshCounter, setHistoryRefreshCounter] = useState(0);
  const [historySelectedAnalysis, setHistorySelectedAnalysis] =
    useState<ContinuousComplianceResponse | null>(null);
  const [historySelectedDocType, setHistorySelectedDocType] = useState<
    "policy" | "emergency" | "survey" | null
  >(null);
  const [historySelectedId, setHistorySelectedId] = useState<number | null>(
    null,
  );
  // Per-type expand/collapse state for Recently Reviewed section
  const [historyExpandedTypes, setHistoryExpandedTypes] = useState<
    Record<"policy" | "emergency" | "survey", boolean>
  >({
    policy: true,
    emergency: true,
    survey: true,
  });

  // Pagination state for Recently Reviewed Documents (per type)
  const [historyPagePolicy, setHistoryPagePolicy] = useState(1);
  const [historyRowsPerPagePolicy, setHistoryRowsPerPagePolicy] = useState(5);
  const [historyPageEmergency, setHistoryPageEmergency] = useState(1);
  const [historyRowsPerPageEmergency, setHistoryRowsPerPageEmergency] =
    useState(5);
  const [historyPageSurvey, setHistoryPageSurvey] = useState(1);
  const [historyRowsPerPageSurvey, setHistoryRowsPerPageSurvey] = useState(5);
  const [historyTotalPolicy, setHistoryTotalPolicy] = useState(0);
  const [historyTotalEmergency, setHistoryTotalEmergency] = useState(0);
  const [historyTotalSurvey, setHistoryTotalSurvey] = useState(0);

  // Fetch analysis history for all document types
  useEffect(() => {
    let cancelled = false;

    const fetchAnalysisHistory = async () => {
      try {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        const facilityId = resolvedFacility?.facility_id || 0;

        if (userId > 0 && facilityId > 0) {
          // React 18 StrictMode runs effects twice in dev; guard to avoid duplicate calls.
          // Include historyRefreshCounter so a manual refresh bypasses the guard.
          const fetchKey = `${userId}-${facilityId}-${historyRefreshCounter}-${historyPagePolicy}-${historyRowsPerPagePolicy}-${historyPageEmergency}-${historyRowsPerPageEmergency}-${historyPageSurvey}-${historyRowsPerPageSurvey}`;
          if (analysisHistoryFetchKeyRef.current === fetchKey) return;
          analysisHistoryFetchKeyRef.current = fetchKey;

          setLoadingHistory(true);

          // Fetch history for each document type separately for proper filtering,
          // using per-type pagination (page + page_size)
          const documentTypes = [
            "Policy and Procedure Review",
            "Emergency Plan Review",
            "Getting Ready for Survey",
          ] as const;

          const allFirstPage =
            historyPagePolicy === 1 &&
            historyPageEmergency === 1 &&
            historyPageSurvey === 1;
          const batchPageSize = Math.max(
            historyRowsPerPagePolicy,
            historyRowsPerPageEmergency,
            historyRowsPerPageSurvey,
          );

          let historyResults: Awaited<
            ReturnType<typeof continuousComplianceService.getAnalysisHistory>
          >[];

          if (allFirstPage) {
            const batch =
              await continuousComplianceService.getAnalysisHistoryBatchFirstPages({
                user_id: userId,
                facility_id: facilityId,
                types: [...documentTypes],
                page_size: batchPageSize,
              });
            if (batch.success && batch.result?.by_type) {
              const by = batch.result.by_type;
              historyResults = documentTypes.map((type) => {
                const bucket = by[type] ?? {
                  data: [],
                  total: 0,
                  page: 1,
                  page_size: batchPageSize,
                };
                return {
                  data: Array.isArray(bucket.data) ? bucket.data : [],
                  total: typeof bucket.total === "number" ? bucket.total : 0,
                  page: typeof bucket.page === "number" ? bucket.page : 1,
                  page_size:
                    typeof bucket.page_size === "number"
                      ? bucket.page_size
                      : batchPageSize,
                };
              });
            } else {
              const historyPromises = documentTypes.map((type) => {
                let page = 1;
                let page_size = 5;
                if (type === "Policy and Procedure Review") {
                  page = historyPagePolicy;
                  page_size = historyRowsPerPagePolicy;
                } else if (type === "Emergency Plan Review") {
                  page = historyPageEmergency;
                  page_size = historyRowsPerPageEmergency;
                } else if (type === "Getting Ready for Survey") {
                  page = historyPageSurvey;
                  page_size = historyRowsPerPageSurvey;
                }

                return continuousComplianceService.getAnalysisHistory({
                  user_id: userId,
                  facility_id: facilityId,
                  type,
                  is_active: true,
                  page,
                  page_size,
                });
              });
              historyResults = await Promise.all(historyPromises);
            }
          } else {
            const historyPromises = documentTypes.map((type) => {
              let page = 1;
              let page_size = 5;
              if (type === "Policy and Procedure Review") {
                page = historyPagePolicy;
                page_size = historyRowsPerPagePolicy;
              } else if (type === "Emergency Plan Review") {
                page = historyPageEmergency;
                page_size = historyRowsPerPageEmergency;
              } else if (type === "Getting Ready for Survey") {
                page = historyPageSurvey;
                page_size = historyRowsPerPageSurvey;
              }

              return continuousComplianceService.getAnalysisHistory({
                user_id: userId,
                facility_id: facilityId,
                type,
                is_active: true,
                page,
                page_size,
              });
            });
            historyResults = await Promise.all(historyPromises);
          }

          if (cancelled) return;

          // Flatten the current-page results into a single array
          const currentPageItems = historyResults.flatMap((r) => r.data || []);

          // Update per-type totals from API response
          historyResults.forEach((result, index) => {
            const type = documentTypes[index];
            if (type === "Policy and Procedure Review") {
              setHistoryTotalPolicy(result.total || 0);
            } else if (type === "Emergency Plan Review") {
              setHistoryTotalEmergency(result.total || 0);
            } else if (type === "Getting Ready for Survey") {
              setHistoryTotalSurvey(result.total || 0);
            }
          });

          console.log("📚 Analysis History fetched (paginated):", {
            results: historyResults,
            combined: currentPageItems,
          });
          setAnalysisHistory(currentPageItems || []);
        }
      } catch (error) {
        console.error("Failed to fetch analysis history:", error);
        if (!cancelled) {
          setAnalysisHistory([]);
        }
      } finally {
        if (!cancelled) {
          setLoadingHistory(false);
        }
      }
    };

    if (resolvedFacility) {
      void fetchAnalysisHistory();
    }

    return () => {
      cancelled = true;
    };
  }, [
    resolvedFacility?.facility_id,
    historyPagePolicy,
    historyRowsPerPagePolicy,
    historyPageEmergency,
    historyRowsPerPageEmergency,
    historyPageSurvey,
    historyRowsPerPageSurvey,
    historyRefreshCounter,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ]);

  const compliancePageAuditLoggedRef = React.useRef<string | null>(null);

  // Log page access when the Continuous Compliance tab is visible
  useEffect(() => {
    if (!complianceTabActive) {
      compliancePageAuditLoggedRef.current = null;
      return;
    }
    const facilityId = resolvedFacility?.facility_id;
    if (!facilityId) return;
    const key = String(facilityId);
    if (compliancePageAuditLoggedRef.current === key) return;
    compliancePageAuditLoggedRef.current = key;

    const logPageAccess = async () => {
      const userId = parseInt(getCurrentUserId(), 10);
      const facility = resolvedFacility;

      await auditLogService.logApiCallToAuditLog({
        user_id: userId,
        endpoint: "/api/v1/continuous-compliance/",
        method: "GET",
        request_payload: {},
        response_payload: {},
        status_code: 200,
        facility_info: facility
          ? {
              facility_id: facility.facility_id,
              facility_name: facility.facility_name,
            }
          : {},
        action_details: {
          page: "Continuous Compliance",
          action: "VIEW",
        },
        action_type: "VIEW",
        action_status: "SUCCESS",
      });
    };

    void logPageAccess().catch((err) => {
      console.warn("Continuous Compliance audit log (page view) failed:", err);
    });
  }, [complianceTabActive, resolvedFacility]);

  // Feature flag from billing / org configuration: when explicitly false,
  // AffableBPM-related UI (Document Manager, Affable accordions, buttons)
  // should be hidden and we should fall back to the default upload accordions.
  const isAffableBpmFeatureEnabled =
    resolvedFacility?.affablebpmconnect === undefined ||
    resolvedFacility?.affablebpmconnect === null
      ? true
      : Boolean(resolvedFacility.affablebpmconnect);

  // Final switch that controls whether any AffableBPM UI should render
  const isAffableUiEnabled = isAffableConnected && isAffableBpmFeatureEnabled;
  const [policyFile, setPolicyFile] = useState<File | null>(null);
  const [emergencyFile, setEmergencyFile] = useState<File | null>(null);
  const [surveyFile, setSurveyFile] = useState<File | null>(null);
  const [, setAffableSourceAccordion] = useState<
    "policy" | "emergency" | "survey" | null
  >(null);

  const [dragOverPolicy, setDragOverPolicy] = useState(false);
  const [dragOverEmergency, setDragOverEmergency] = useState(false);
  const [dragOverSurvey, setDragOverSurvey] = useState(false);

  const [policyAnalysis, setPolicyAnalysis] =
    useState<ContinuousComplianceResponse | null>(null);
  const [emergencyAnalysis, setEmergencyAnalysis] =
    useState<ContinuousComplianceResponse | null>(null);
  const [surveyAnalysis, setSurveyAnalysis] =
    useState<ContinuousComplianceResponse | null>(null);

  const [loadingPolicy, setLoadingPolicy] = useState(false);
  const [loadingEmergency, setLoadingEmergency] = useState(false);
  const [loadingSurvey, setLoadingSurvey] = useState(false);

  const [errorPolicy, setErrorPolicy] = useState<string | null>(null);
  const [errorEmergency, setErrorEmergency] = useState<string | null>(null);
  const [errorSurvey, setErrorSurvey] = useState<string | null>(null);

  const [policyUploadDate, setPolicyUploadDate] = useState<string | null>(null);
  const [emergencyUploadDate, setEmergencyUploadDate] = useState<string | null>(
    null,
  );
  const [surveyUploadDate, setSurveyUploadDate] = useState<string | null>(null);

  const [showPolicyReport, setShowPolicyReport] = useState(false);
  const [showEmergencyReport, setShowEmergencyReport] = useState(false);
  const [showSurveyReport, setShowSurveyReport] = useState(false);
  const [tabExpandedPanel, setTabExpandedPanel] = useState<string | false>(false);
  const complianceRecentlyReviewedAutoOpenedRef = useRef(false);

  const handleTabAccordionChange =
    (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setTabExpandedPanel(isExpanded ? panel : false);
    };

  useEffect(() => {
    if (loadingHistory) {
      return;
    }
    if (analysisHistory.length === 0) {
      setTabExpandedPanel((prev) =>
        prev === "recentlyReviewed" ? false : prev,
      );
      complianceRecentlyReviewedAutoOpenedRef.current = false;
      return;
    }
    if (!complianceRecentlyReviewedAutoOpenedRef.current) {
      complianceRecentlyReviewedAutoOpenedRef.current = true;
      setTabExpandedPanel((prev) =>
        prev === false ? "recentlyReviewed" : prev,
      );
    }
  }, [loadingHistory, analysisHistory.length]);

  const [isSyncingAffableRecords, setIsSyncingAffableRecords] = useState(false);
  const [affableSyncError, setAffableSyncError] = useState<string | null>(null);
  const [affableDocuments, setAffableDocuments] = useState<
    AffableDocumentItem[]
  >([]);
  const [affableRecords, setAffableRecords] = useState<AffableRecordItem[]>([]);
  const [selectedAffableDocumentId, setSelectedAffableDocumentId] = useState<
    number | null
  >(null);
  const [selectedAffableFolderId, setSelectedAffableFolderId] = useState<
    number | null
  >(null);
  const [affableRecordCount, setAffableRecordCount] = useState(0);
  const [hasAttemptedAffableSync, setHasAttemptedAffableSync] = useState(false);
  const [selectedAffableFileKey, setSelectedAffableFileKey] = useState<
    string | null
  >(null);
  const [loadingAffableFile, setLoadingAffableFile] = useState(false);
  const [errorAffableFile, setErrorAffableFile] = useState<string | null>(null);
  const [isAffablePanelClosed, setIsAffablePanelClosed] = useState(false);

  // Separate state for AffableBPM file analysis
  const [affableFileAnalysis, setAffableFileAnalysis] =
    useState<ContinuousComplianceResponse | null>(null);
  const [affableFile, setAffableFile] = useState<File | null>(null);
  const [affableFileUploadDate, setAffableFileUploadDate] = useState<
    string | null
  >(null);
  const [showAffableFileReport, setShowAffableFileReport] = useState(false);
  const [affableUploadContext, setAffableUploadContext] = useState<{
    documentId: number;
    folderId: number;
    orgName: string;
    deptId?: number;
    orgId?: number;
  } | null>(null);
  const [isUploadingAffableResult, setIsUploadingAffableResult] =
    useState(false);
  const [, setAffableResultUploadError] = useState<
    string | null
  >(null);
  const [, setAffableResultUploadSuccess] = useState<
    string | null
  >(null);
  const [affableDocsPage, setAffableDocsPage] = useState(1);
  const [affableRowsPerPage, setAffableRowsPerPage] = useState(10);
  const [downloadMenuAnchor, setDownloadMenuAnchor] =
    useState<null | HTMLElement>(null);

  useEffect(() => {
    if (!focusUploadSection) return;
    setTabExpandedPanel(focusUploadSection);
    requestAnimationFrame(() => {
      document
        .getElementById(`${focusUploadSection}-header`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
    onFocusUploadSectionConsumed?.();
  }, [focusUploadSection, onFocusUploadSectionConsumed]);

  // Clear all analysis state when facility changes
  const prevFacilityIdRef = useRef<number | undefined>(
    resolvedFacility?.facility_id,
  );
  useEffect(() => {
    const currentId = resolvedFacility?.facility_id;
    if (
      prevFacilityIdRef.current !== undefined &&
      prevFacilityIdRef.current !== currentId
    ) {
      // Facility changed — reset uploaded files, analysis results, and reports
      setPolicyFile(null);
      setPolicyAnalysis(null);
      setPolicyUploadDate(null);
      setErrorPolicy(null);
      setShowPolicyReport(false);

      setEmergencyFile(null);
      setEmergencyAnalysis(null);
      setEmergencyUploadDate(null);
      setErrorEmergency(null);
      setShowEmergencyReport(false);

      setSurveyFile(null);
      setSurveyAnalysis(null);
      setSurveyUploadDate(null);
      setErrorSurvey(null);
      setShowSurveyReport(false);

      // Reset AffableBPM state
      setAffableDocuments([]);
      setAffableRecords([]);
      setAffableRecordCount(0);
      setAffableSyncError(null);
      setAffableFile(null);
      setAffableFileAnalysis(null);
      setAffableFileUploadDate(null);
      setErrorAffableFile(null);
      setAffableResultUploadError(null);
      setAffableResultUploadSuccess(null);

      // Reset history selection
      setHistorySelectedAnalysis(null);
      setHistorySelectedDocType(null);
      setHistorySelectedId(null);

      complianceRecentlyReviewedAutoOpenedRef.current = false;
      setTabExpandedPanel((prev) =>
        prev === "recentlyReviewed" ? false : prev,
      );
    }
    prevFacilityIdRef.current = currentId;
  }, [resolvedFacility?.facility_id]);

  useEffect(() => {
    if (!affableDocuments.length) {
      setSelectedAffableDocumentId(null);
      setSelectedAffableFolderId(null);
      return;
    }

    setSelectedAffableDocumentId((prevDocId) => {
      const nextDoc =
        prevDocId !== null &&
        affableDocuments.some((document) => document.id === prevDocId)
          ? affableDocuments.find((document) => document.id === prevDocId)
          : affableDocuments[0];

      setSelectedAffableFolderId(
        nextDoc && nextDoc.folders.length ? nextDoc.folders[0].id : null,
      );

      return nextDoc ? nextDoc.id : null;
    });
  }, [affableDocuments]);

  useEffect(() => {
    if (!selectedAffableDocumentId) {
      setSelectedAffableFolderId(null);
      return;
    }

    const currentDoc = affableDocuments.find(
      (document) => document.id === selectedAffableDocumentId,
    );
    if (!currentDoc) {
      setSelectedAffableFolderId(null);
      return;
    }

    setSelectedAffableFolderId((prevFolderId) => {
      const stillExists =
        prevFolderId !== null &&
        currentDoc.folders.some((folder) => folder.id === prevFolderId);

      const nextFolderId = stillExists
        ? prevFolderId
        : currentDoc.folders.length
          ? currentDoc.folders[0].id
          : null;

      return nextFolderId;
    });
  }, [affableDocuments, selectedAffableDocumentId]);

  // Clear any selected file when document changes,
  // but keep selection when only the folder changes.
  useEffect(() => {
    setSelectedAffableFileKey(null);
  }, [selectedAffableDocumentId]);

  const handleFileUpload = async (
    file: File,
    type: "policy" | "emergency" | "survey",
  ) => {
    const setFile =
      type === "policy"
        ? setPolicyFile
        : type === "emergency"
          ? setEmergencyFile
          : setSurveyFile;
    const setLoading =
      type === "policy"
        ? setLoadingPolicy
        : type === "emergency"
          ? setLoadingEmergency
          : setLoadingSurvey;
    const setError =
      type === "policy"
        ? setErrorPolicy
        : type === "emergency"
          ? setErrorEmergency
          : setErrorSurvey;
    const setAnalysis =
      type === "policy"
        ? setPolicyAnalysis
        : type === "emergency"
          ? setEmergencyAnalysis
          : setSurveyAnalysis;
    const setUploadDate =
      type === "policy"
        ? setPolicyUploadDate
        : type === "emergency"
          ? setEmergencyUploadDate
          : setSurveyUploadDate;

    // Keep the active upload accordion open through analysis and history refresh
    setTabExpandedPanel(type);

    // Ensure loader shows immediately on re-upload
    setLoading(true);
    setError(null);
    setAnalysis(null);
    if (type === "policy") setShowPolicyReport(false);
    else if (type === "emergency") setShowEmergencyReport(false);
    else setShowSurveyReport(false);
    setFile(null);
    setFile(file);
    setUploadDate(formatClientUploadTimestamp(new Date()));

    try {
      // Map document types for the compliance analysis API
      const documentTypeMap: Record<"policy" | "emergency" | "survey", string> =
        {
          policy: "Policy and Procedure",
          emergency: "Policy and Procedure",
          survey: "Policy and Procedure",
        };

      // Get facility data with fallbacks (from prop or sessionStorage)
      const currentFacilityData = getFacilityData();

      // Debug logging to check facility data
      console.log("🏥 Facility Data for Compliance Analysis:", {
        fromProp: !!facilityData,
        fromSessionStorage: !facilityData,
        facilityData: currentFacilityData,
        state: currentFacilityData?.state,
        city: currentFacilityData?.city,
        facility_type: currentFacilityData?.facility_type,
      });

      const facilityType =
        currentFacilityData?.facility_type_slug ||
        currentFacilityData?.facility_type ||
        "Rural Health Clinic"; // Prefer slug for pattern matching
      const country = getSelectedCountryCode() || "USA";
      // Extract state and city with extra fallbacks to sessionStorage
      const sessionState = sessionStorage.getItem("sessionState") || "";
      const sessionCity = sessionStorage.getItem("sessionCity") || "";
      const state = currentFacilityData?.state
        ? String(currentFacilityData.state).trim()
        : sessionState.trim();
      const city = currentFacilityData?.city
        ? String(currentFacilityData.city).trim()
        : sessionCity.trim();
      const currentYear = new Date().getFullYear().toString();

      console.log("📋 Prepared Compliance Analysis Payload:", {
        facility_type: facilityType,
        country: country,
        state: state || "(empty)",
        stateSource: state
          ? "facility"
          : sessionState
            ? "sessionState"
            : "missing",
        city: city || "(empty)",
        citySource: city ? "facility" : sessionCity ? "sessionCity" : "missing",
        current_year: currentYear,
      });

      const analysis =
        await continuousComplianceService.analyzeDocumentForCompliance({
          file,
          facility_type: facilityType,
          country: country,
          state: state,
          city: city,
          current_year: currentYear,
          task: "survey readiness preparation",
          document_type: documentTypeMap[type],
        });
      setAnalysis(analysis);

      // Save analysis history, then refresh the list from the API
      try {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        const facilityId = currentFacilityData?.facility_id || 0;

        // Map document types for analysis history
        const typeMap: Record<"policy" | "emergency" | "survey", string> = {
          policy: "Policy and Procedure Review",
          emergency: "Emergency Plan Review",
          survey: "Getting Ready for Survey",
        };

        await continuousComplianceService.saveAnalysisHistory({
          user_id: userId,
          facility_id: facilityId,
          type: typeMap[type],
          data_obj: {
            analysis: analysis.analysis || analysis,
            metadata: analysis.metadata || {},
          },
        });

        // Trigger a re-fetch of the history list so the new entry appears
        setHistoryRefreshCounter((c) => c + 1);
      } catch (historyError) {
        console.error("Failed to save analysis history:", historyError);
        // Don't block the main flow if history save fails
      }
    } catch (error) {
      console.error(`${type} analysis failed:`, error);

      // Check if it's an authentication error
      if (
        error instanceof Error &&
        error.message.includes("Not authenticated")
      ) {
        setError("Session expired. Please sign in again.");
        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setError(error instanceof Error ? error.message : "Analysis failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDrop = (
    e: React.DragEvent<HTMLDivElement>,
    type: "policy" | "emergency" | "survey",
  ) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0], type);
    }
    if (type === "policy") setDragOverPolicy(false);
    else if (type === "emergency") setDragOverEmergency(false);
    else setDragOverSurvey(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleDragEnter = (
    e: React.DragEvent<HTMLDivElement>,
    type: "policy" | "emergency" | "survey",
  ) => {
    e.preventDefault();
    if (type === "policy") setDragOverPolicy(true);
    else if (type === "emergency") setDragOverEmergency(true);
    else setDragOverSurvey(true);
  };

  const handleDragLeave = (
    e: React.DragEvent<HTMLDivElement>,
    type: "policy" | "emergency" | "survey",
  ) => {
    e.preventDefault();
    if (type === "policy") setDragOverPolicy(false);
    else if (type === "emergency") setDragOverEmergency(false);
    else setDragOverSurvey(false);
  };

  const handleDeleteFile = (type: "policy" | "emergency" | "survey") => {
    if (type === "policy") {
      setPolicyFile(null);
      setPolicyAnalysis(null);
      setPolicyUploadDate(null);
      setErrorPolicy(null);
      setShowPolicyReport(false);
    } else if (type === "emergency") {
      setEmergencyFile(null);
      setEmergencyAnalysis(null);
      setEmergencyUploadDate(null);
      setErrorEmergency(null);
      setShowEmergencyReport(false);
    } else {
      setSurveyFile(null);
      setSurveyAnalysis(null);
      setSurveyUploadDate(null);
      setErrorSurvey(null);
      setShowSurveyReport(false);
    }
  };

  const getUploadDate = (type: "policy" | "emergency" | "survey") => {
    return type === "policy"
      ? policyUploadDate
      : type === "emergency"
        ? emergencyUploadDate
        : surveyUploadDate;
  };

  const toggleReport = (type: "policy" | "emergency" | "survey") => {
    if (type === "policy") {
      setShowPolicyReport(!showPolicyReport);
      if (!showPolicyReport) {
        setShowEmergencyReport(false);
        setShowSurveyReport(false);
        setShowAffableFileReport(false);
      }
    } else if (type === "emergency") {
      setShowEmergencyReport(!showEmergencyReport);
      if (!showEmergencyReport) {
        setShowPolicyReport(false);
        setShowSurveyReport(false);
        setShowAffableFileReport(false);
      }
    } else {
      setShowSurveyReport(!showSurveyReport);
      if (!showSurveyReport) {
        setShowPolicyReport(false);
        setShowEmergencyReport(false);
        setShowAffableFileReport(false);
      }
    }
  };

  const getShowReport = (type: "policy" | "emergency" | "survey") => {
    return type === "policy"
      ? showPolicyReport
      : type === "emergency"
        ? showEmergencyReport
        : showSurveyReport;
  };

  const renderReportDetails = (
    analysis: ContinuousComplianceResponse,
    type: "policy" | "emergency" | "survey",
    options?: { enableAffableUploadButton?: boolean },
  ) => {
    const data: any = (analysis as any)?.analysis || {};
    const facilityInfo = data?.facility_info || {};
    const facilityName =
      facilityInfo?.facility_name ||
      facilityData?.site_name ||
      "Evergreen Health Medical Center";
    const facilityCity = facilityInfo?.city || facilityData?.city || "Kirkland";
    const facilityState = facilityInfo?.state || facilityData?.state || "WA";
    const facilityCountry = facilityInfo?.country || getSelectedCountryCode();
    const facilityType =
      facilityInfo?.facility_type ||
      data?.document_type ||
      facilityData?.facility_type ||
      "Clinic";
    const facilityYear =
      facilityInfo?.current_year ||
      data?.current_year ||
      new Date().getFullYear().toString();
    const facilityIndustry = facilityInfo?.industry || "";

    return (
      <ReportDetailsContainer>
        {/* <ReportHeader>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: "8px",
              flex: 1,
            }}
          >
            <Box
              sx={{
                width: "20px",
                height: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
              >
                <path
                  d="M5.75 4C4.7835 4 4 4.7835 4 5.75V13.5C4 14.8807 5.11929 16 6.5 16H13.5C14.8807 16 16 14.8807 16 13.5V12H13V5.75C13 4.7835 12.2165 4 11.25 4H5.75ZM13 13H15V13.5C15 14.3284 14.3284 15 13.5 15H13V13ZM5 5.75C5 5.33579 5.33579 5 5.75 5H11.25C11.6642 5 12 5.33579 12 5.75V15H6.5C5.67157 15 5 14.3284 5 13.5V5.75ZM6.5 7.5C6.5 7.22386 6.72386 7 7 7H10C10.2761 7 10.5 7.22386 10.5 7.5C10.5 7.77614 10.2761 8 10 8H7C6.72386 8 6.5 7.77614 6.5 7.5ZM6.5 10C6.5 9.72386 6.72386 9.5 7 9.5H10C10.2761 9.5 10.5 9.72386 10.5 10C10.5 10.2761 10.2761 10.5 10 10.5H7C6.72386 10.5 6.5 10.2761 6.5 10ZM7 12C6.72386 12 6.5 12.2239 6.5 12.5C6.5 12.7761 6.72386 13 7 13H8.5C8.77614 13 9 12.7761 9 12.5C9 12.2239 8.77614 12 8.5 12H7Z"
                  fill="#065B70"
                />
              </svg>
            </Box>
            <ReportHeaderInfo>
              {(() => {
                const uiTitleMap: Record<typeof type, string> = {
                  policy: "Policy & Procedure Review",
                  emergency: "Emergency Plan Review",
                  survey: "Survey Review",
                } as const;
                return <ReportTitle>{uiTitleMap[type]}</ReportTitle>;
              })()}
              <FacilityInfo>
                Facility: {facilityName} – {facilityCity || "N/A"},{" "}
                {facilityState || "N/A"} ({facilityCountry || "N/A"})
              </FacilityInfo>
            </ReportHeaderInfo>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "8px",
            }}
          >
            <LastUpdated>
              Last updated:{" "}
              {analysis.metadata.analysis_timestamp
                ? new Date(
                    analysis.metadata.analysis_timestamp
                  ).toLocaleDateString("en-US", {
                    month: "long",
                    day: "2-digit",
                    year: "numeric",
                  })
                : getUploadDate(type)}
            </LastUpdated>
            <DownloadButton onClick={() => DownloadReport(type, analysis)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                style={{ marginRight: 8 }}
              >
                <path
                  d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.5 10.5l4.5 4.5 4.5-4.5"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12 3v12"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Download
            </DownloadButton>
            <CloseButton onClick={() => toggleReport(type)}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
              >
                <path
                  d="M9 3L3 9M3 3L9 9"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </CloseButton>
          </Box>
        </ReportHeader> */}
        <Box
          sx={{
            display: "flex",
            gap: "12px",
            marginBottom: "12px",
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <DownloadButton
            onClick={(e) => setDownloadMenuAnchor(e.currentTarget)}
            endIcon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  d="M6 9l6 6 6-6"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          >
            Download
          </DownloadButton>
          <Menu
            anchorEl={downloadMenuAnchor}
            open={Boolean(downloadMenuAnchor)}
            onClose={() => setDownloadMenuAnchor(null)}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            PaperProps={{
              sx: {
                borderRadius: "8px",
                boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
                minWidth: "120px",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                DownloadReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4.49976 9.00299C4.22361 9.00299 3.99976 9.22685 3.99976 9.50299V11.503C3.99976 11.7791 4.22361 12.003 4.49976 12.003C4.7759 12.003 4.99976 11.7791 4.99976 11.503V11.3366H5.33296C5.97736 11.3366 6.49976 10.8142 6.49976 10.1698C6.49976 9.52538 5.97736 9.00299 5.33296 9.00299H4.49976ZM5.33296 10.3366H4.99976V10.003H5.33296C5.42508 10.003 5.49976 10.0777 5.49976 10.1698C5.49976 10.2619 5.42508 10.3366 5.33296 10.3366ZM10.0005 9.50236C10.0008 9.22646 10.2246 9.00299 10.5005 9.00299H11.4988C11.7749 9.00299 11.9988 9.22685 11.9988 9.50299C11.9988 9.77913 11.7749 10.003 11.4988 10.003H10.9998L10.9994 10.3375H11.4988C11.7749 10.3375 11.9988 10.5613 11.9988 10.8375C11.9988 11.1136 11.7749 11.3375 11.4988 11.3375H11L11.0005 11.5017C11.0012 11.7779 10.7779 12.0023 10.5017 12.003C10.2256 12.0037 10.0012 11.7804 10.0005 11.5043L9.99878 10.8368L10.0005 9.50236ZM7.49829 9.00299C7.22215 9.00299 6.99829 9.22685 6.99829 9.50299V11.503C6.99829 11.7791 7.22215 12.003 7.49829 12.003H7.99974C8.82816 12.003 9.49973 11.3314 9.49974 10.503C9.49974 9.67457 8.82817 9.00299 7.99974 9.00299H7.49829ZM7.99829 11.003V10.003H7.99974C8.27588 10.003 8.49974 10.2269 8.49974 10.503C8.49974 10.7791 8.27588 11.003 7.99974 11.003H7.99829ZM3 3.50195V7.08413C2.4174 7.29005 2 7.84567 2 8.49878V12.502C2 13.3304 2.67157 14.002 3.5 14.002H12.5C13.3284 14.002 14 13.3304 14 12.502V8.49878C14 7.84567 13.5826 7.29005 13 7.08413V5.4133C13 5.0149 12.8415 4.63287 12.5595 4.35147L10.6439 2.44012C10.3627 2.15953 9.98167 2.00195 9.58442 2.00195H4.5C3.67157 2.00195 3 2.67353 3 3.50195ZM4.5 3.00195H9V4.49994C9 5.32837 9.67157 5.99994 10.5 5.99994H12V6.99878H4V3.50195C4 3.22581 4.22386 3.00195 4.5 3.00195ZM10 3.21029L11.7936 4.99994H10.5C10.2239 4.99994 10 4.77608 10 4.49994V3.21029ZM3.5 7.99878H12.5C12.7761 7.99878 13 8.22264 13 8.49878V12.502C13 12.7781 12.7761 13.002 12.5 13.002H3.5C3.22386 13.002 3 12.7781 3 12.502V8.49878C3 8.22264 3.22386 7.99878 3.5 7.99878Z"
                  fill="#262626"
                />
              </svg>
              PDF
            </MenuItem>
            <MenuItem
              onClick={() => {
                DownloadDocxReport(type, analysis);
                setDownloadMenuAnchor(null);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontFamily: "Rethink Sans, sans-serif",
                padding: "10px 16px",
              }}
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 3C3 1.89543 3.89543 1 5 1H8.58579C8.98361 1 9.36514 1.15804 9.64645 1.43934L12.5607 4.35355C12.842 4.63486 13 5.01639 13 5.41421V13C13 14.1046 12.1046 15 11 15H5C3.89543 15 3 14.1046 3 13V3ZM5 2C4.44772 2 4 2.44772 4 3V13C4 13.5523 4.44772 14 5 14H11C11.5523 14 12 13.5523 12 13V6H9.5C8.67157 6 8 5.32843 8 4.5V2H5ZM9.5 5H11.7929L9 2.20711V4.5C9 4.77614 9.22386 5 9.5 5ZM5.5 8C5.22386 8 5 8.22386 5 8.5C5 8.77614 5.22386 9 5.5 9H10.5C10.7761 9 11 8.77614 11 8.5C11 8.22386 10.7761 8 10.5 8H5.5ZM5 10.5C5 10.2239 5.22386 10 5.5 10H10.5C10.7761 10 11 10.2239 11 10.5C11 10.7761 10.7761 11 10.5 11H5.5C5.22386 11 5 10.7761 5 10.5ZM5.5 12C5.22386 12 5 12.2239 5 12.5C5 12.7761 5.22386 13 5.5 13H10.5C10.7761 13 11 12.7761 11 12.5C11 12.2239 10.7761 12 10.5 12H5.5Z"
                  fill="#262626"
                />
              </svg>
              Word
            </MenuItem>
          </Menu>
          {options?.enableAffableUploadButton && (
            <UploadActionButton
              onClick={handleAffableResultUploadButtonClick}
              disabled={isUploadingAffableResult}
              sx={{ display: "flex", alignItems: "center", gap: "8px" }}
            >
              {isUploadingAffableResult ? (
                <>
                  <CircularProgress size={16} sx={{ color: "#0a829d" }} />
                  Uploading...
                </>
              ) : (
                <>
                  {/* <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M7.33333 9.66667V5.276L5.47133 7.13867L4.52867 6.196L8 2.72467L11.4713 6.196L10.5287 7.13867L8.66667 5.276V9.66667H7.33333ZM4 12.3333H12V13.6667H4V12.3333Z"
                      fill="#065B70"
                    />
                  </svg> */}
                  <AffableConnectedIcon />
                  Upload to AffableFLOW
                </>
              )}
            </UploadActionButton>
          )}
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "3px",
            marginTop: "3px",
          }}
        >
          {/* Summary Section */}
          {/* <StyledAccordion defaultExpanded={true}>
            <AccordionSummary
              expandIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              }
              sx={{
                minHeight: "56px",
                "&.Mui-expanded": {
                  minHeight: "56px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                  "&.Mui-expanded": {
                    margin: "12px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle className="accordion-title">
                Summary of {type === "policy" ? "Policy" : type === "emergency" ? "Emergency" : "Survey"} Reports
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#fffbf0",
                  border: "1px solid #f59e0b",
                  borderRadius: "4px",
                  padding: "8px",
                  marginBottom: "16px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <Box
                    sx={{
                      width: "20px",
                      height: "20px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M8.68149 2.78544C9.24892 1.73813 10.752 1.73821 11.3193 2.78557L17.8198 14.7865C18.3612 15.786 17.6375 17.0009 16.5009 17.0009H3.4982C2.36147 17.0009 1.63783 15.7858 2.17934 14.7864L8.68149 2.78544ZM10.5 7.5C10.5 7.22386 10.2761 7 10 7C9.72386 7 9.5 7.22386 9.5 7.5V11.5C9.5 11.7761 9.72386 12 10 12C10.2761 12 10.5 11.7761 10.5 11.5V7.5ZM10.75 13.75C10.75 13.3358 10.4142 13 10 13C9.58579 13 9.25 13.3358 9.25 13.75C9.25 14.1642 9.58579 14.5 10 14.5C10.4142 14.5 10.75 14.1642 10.75 13.75Z"
                        fill="url(#paint0_linear_2570_14894)"
                      />
                      <defs>
                        <linearGradient
                          id="paint0_linear_2570_14894"
                          x1="9.99954"
                          y1="2"
                          x2="9.99954"
                          y2="17.0009"
                          gradientUnits="userSpaceOnUse"
                        >
                          <stop stop-color="#EF7A19" />
                          <stop offset="1" stop-color="#ED6C02" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </Box>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#262626" }}
                  >
                    Gaps found
                  </Typography>
                </Box>

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                    marginBottom: "12px",
                  }}
                >
                  <strong>
                    Risk Score: {data.risk_score?.overall_score || 0}/100 (
                    {data.risk_score?.risk_level || "MEDIUM"})
                  </strong>
                  <br />
                  <br />
                  {data.executive_summary || "No executive summary available"}
                </Typography>

                {data.incident_summary && (
                  <Typography
                    sx={{ fontSize: "14px", color: "#262626", lineHeight: 1.5 }}
                  >
                    <strong>
                      {data.incident_summary.incident_type}:
                    </strong>{" "}
                    {data.incident_summary.description}
                    <br />
                    <br />
                    <strong>Date:</strong> {data.incident_summary.date}
                    <br />
                    <strong>Location:</strong>{" "}
                    {data.incident_summary.location}
                    <br />
                    <strong>Documentation Status:</strong>{" "}
                    {data.incident_summary.documentation_completeness}
                  </Typography>
                )}
              </Box>
            </AccordionDetails>
          </StyledAccordion> */}

          {/* Facility Context Section */}
          {/* {(facilityInfo?.facility_type ||
            facilityInfo?.country ||
            facilityInfo?.state ||
            facilityInfo?.city ||
            facilityInfo?.current_year ||
            facilityInfo?.industry) && (
            <StyledAccordion defaultExpanded={false}>
              <AccordionSummary
                expandIcon={
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M5 7.5L10 12.5L15 7.5" stroke="#262626" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                }
                sx={{
                  minHeight: "56px",
                  "&.Mui-expanded": {
                    minHeight: "56px",
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "12px 0",
                    "&.Mui-expanded": {
                      margin: "12px 0",
                    },
                  },
                  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                    transform: "rotate(180deg)",
                  },
                }}
              >
                <AccordionTitle className="accordion-title">
                  Facility Context
                </AccordionTitle>
              </AccordionSummary>
              <AccordionDetails
                sx={{
                  padding: "16px",
                  backgroundColor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "12px",
                  }}
                >
                  {[
                    { label: "Facility Type", value: facilityType },
                    { label: "Country", value: facilityCountry },
                    { label: "State", value: facilityState },
                    { label: "City", value: facilityCity },
                    { label: "Current Year", value: facilityYear },
                    { label: "Industry", value: facilityIndustry || "N/A" },
                  ].map((item, idx) => (
                    <Box
                      key={`${item.label}-${idx}`}
                      sx={{
                        backgroundColor: "#f3fdff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "12px",
                      }}
                    >
                      <Typography sx={{ fontSize: "12px", color: "#737373" }}>
                        {item.label}
                      </Typography>
                      <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                        {item.value || "N/A"}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </AccordionDetails>
            </StyledAccordion>
          )} */}

          {/* Core Regulatory References Section */}
          {Array.isArray(data.core_regulatory_references) &&
            data.core_regulatory_references.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Core Regulatory References</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.core_regulatory_references.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.core_regulatory_references.map(
                    (reference: any, index: number) => (
                      <Box
                        key={`core-reference-${index}`}
                        sx={{
                          backgroundColor: "#f3fdff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#262626",
                            borderBottom: "2px solid #3095AB",
                          }}
                        >
                          {reference.regulatory_source_title || "Reference"}
                        </Typography>
                        {reference.reference_url_link && (
                          <Link
                            href={reference.reference_url_link}
                            target="_blank"
                            rel="noopener"
                            sx={{
                              fontSize: "13px",
                              display: "inline-flex",
                              gap: "4px",
                              padding: "5px",
                              paddingTop: "10px",
                            }}
                          >
                            {reference.reference_url_link}
                          </Link>
                        )}
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#262626",
                            marginTop: "8px",
                          }}
                        >
                          {reference.key_relevance ||
                            "No description provided."}
                        </Typography>
                      </Box>
                    ),
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Mock Survey Readiness Matrix Section */}
          {Array.isArray(data.mock_survey_readiness_matrix) &&
            data.mock_survey_readiness_matrix.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      "&.Mui-expanded": { margin: "7px 0" },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Mock Survey Readiness Matrix</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.mock_survey_readiness_matrix.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ padding: "16px", backgroundColor: "#ffffff" }}
                >
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{ border: "1px solid #e5e7eb" }}
                  >
                    <Table>
                      <TableHead>
                        <TableRow
                          sx={{ backgroundColor: "#0A829D", height: "auto" }}
                        >
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#fff",
                              borderBottom: "2px solid #e5e7eb",
                              padding: "2px 16px",
                            }}
                          >
                            Category
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#fff",
                              borderBottom: "2px solid #e5e7eb",
                              padding: "2px 16px",
                            }}
                          >
                            Original Coverage
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#fff",
                              borderBottom: "2px solid #e5e7eb",
                              padding: "2px 16px",
                            }}
                          >
                            Compliance Gap
                          </TableCell>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              fontSize: "14px",
                              color: "#fff",
                              borderBottom: "2px solid #e5e7eb",
                              padding: "2px 16px",
                            }}
                          >
                            Corrective Action
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.mock_survey_readiness_matrix.map(
                          (item: any, index: number) => (
                            <TableRow
                              key={`mock-matrix-${index}`}
                              sx={{
                                "&:nth-of-type(odd)": {
                                  backgroundColor: "#fafafa",
                                },
                                "&:hover": { backgroundColor: "#f3fdff" },
                              }}
                            >
                              <TableCell
                                sx={{ fontSize: "14px", color: "#262626" }}
                              >
                                {item.category || "N/A"}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#262626" }}
                              >
                                {item.original_document_year_coverage || "N/A"}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#A4262C" }}
                              >
                                {item.current_year_compliance_gap || "N/A"}
                              </TableCell>
                              <TableCell
                                sx={{ fontSize: "14px", color: "#262626" }}
                              >
                                {item.corrective_action_to_update || "N/A"}
                              </TableCell>
                            </TableRow>
                          ),
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Risk Deficiency Exposure Section */}
          {Array.isArray(data.risk_deficiency_exposure) &&
            data.risk_deficiency_exposure.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Risk & Deficiency Exposure</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.risk_deficiency_exposure.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.risk_deficiency_exposure.map(
                    (item: any, index: number) => (
                      <Box
                        key={`risk-exposure-${index}`}
                        sx={{
                          backgroundColor: "#f3fdff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography sx={{ fontSize: "15px", fontWeight: 600 }}>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            viewBox="0 0 20 20"
                            fill="none"
                            style={{
                              verticalAlign: "middle",
                              marginRight: "4px",
                            }}
                          >
                            <path
                              d="M8.68149 2.78544C9.24892 1.73813 10.752 1.73821 11.3193 2.78557L17.8198 14.7865C18.3612 15.786 17.6375 17.0009 16.5009 17.0009H3.4982C2.36147 17.0009 1.63783 15.7858 2.17934 14.7864L8.68149 2.78544ZM10.5 7.5C10.5 7.22386 10.2761 7 10 7C9.72386 7 9.5 7.22386 9.5 7.5V11.5C9.5 11.7761 9.72386 12 10 12C10.2761 12 10.5 11.7761 10.5 11.5V7.5ZM10.75 13.75C10.75 13.3358 10.4142 13 10 13C9.58579 13 9.25 13.3358 9.25 13.75C9.25 14.1642 9.58579 14.5 10 14.5C10.4142 14.5 10.75 14.1642 10.75 13.75Z"
                              fill="#ED6C02"
                            />
                          </svg>
                          {item.risk_area || "Risk Area"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            padding: "8px",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "12px",
                            alignSelf: "stretch",
                            borderRadius: "8px",
                            border: "1px solid #5BBFD6",
                            background: "#FDE7E9",
                            marginTop: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065B70",
                              borderBottom: "1px solid #5BBFD6",
                              paddingBottom: "4px",
                              width: "100%",
                            }}
                          >
                            Potential Deficiency:
                          </Typography>
                          <Typography sx={{ fontSize: "14px" }}>
                            {item.potential_deficiency || "N/A"}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            padding: "8px",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "12px",
                            alignSelf: "stretch",
                            borderRadius: "8px",
                            border: "1px solid #5BBFD6",
                            background: "#DFF6DD",
                            marginTop: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#065B70",
                              borderBottom: "1px solid #065B70",
                              paddingBottom: "4px",
                              width: "100%",
                            }}
                          >
                            Preventative Action:
                          </Typography>
                          <Typography sx={{ fontSize: "14px" }}>
                            {item.preventative_action || "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    ),
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* State / Emirate Requirements Section */}
          {Array.isArray(data.state_specific_requirements) &&
            data.state_specific_requirements.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>State Specific Requirements</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.state_specific_requirements.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.state_specific_requirements.map(
                    (requirement: any, index: number) => (
                      <Box
                        key={`state-req-${index}`}
                        sx={{
                          backgroundColor: "#f3fdff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            color: "#262626",
                            borderBottom: "2px solid #3095AB",
                          }}
                        >
                          {requirement.topic || "Requirement"}
                        </Typography>
                        {requirement.state_reference_url && (
                          <Link
                            href={requirement.state_reference_url}
                            target="_blank"
                            rel="noopener"
                            sx={{
                              fontSize: "13px",
                              display: "inline-flex",
                              gap: "4px",
                              padding: "5px",
                              paddingTop: "10px",
                            }}
                          >
                            {requirement.state_reference_url}
                          </Link>
                        )}
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#262626",
                            marginTop: "8px",
                          }}
                        >
                          <strong>Action:</strong>{" "}
                          {requirement.action || "No action provided."}
                        </Typography>
                      </Box>
                    ),
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Survey Readiness Checklist Section */}
          {Array.isArray(data.survey_readiness_checklist) &&
            data.survey_readiness_checklist.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Survey Readiness Checklist</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.survey_readiness_checklist.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.survey_readiness_checklist.map(
                    (item: any, index: number) => (
                      <Box
                        key={`readiness-item-${index}`}
                        sx={{
                          backgroundColor: "#f3fdff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "16px",
                            borderBottom: "2px solid #3095AB",
                            paddingBottom: "8px",
                            marginBottom: "12px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "15px",
                              fontWeight: 600,
                              flex: 1,
                            }}
                          >
                            {item.item || "Checklist Item"}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              padding: "4px 8px",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "4px",
                              borderRadius: "16px",
                              border:
                                item.status === "Completed"
                                  ? "1px solid #107C10"
                                  : item.status === "Pending"
                                    ? "1px solid #8B4409"
                                    : "1px solid #e5e7eb",
                              background:
                                item.status === "Completed"
                                  ? "#DFF6DD"
                                  : item.status === "Pending"
                                    ? "#FFF4E5"
                                    : "#f3f3f3",
                            }}
                          >
                            <Typography sx={{ fontSize: "14px" }}>
                              {item.status || "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                        {item.notes && (
                          <Typography
                            sx={{ fontSize: "14px", marginTop: "4px" }}
                          >
                            <strong>Notes:</strong> {item.notes}
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Suggested Policy Addenda Section */}
          {Array.isArray(data.suggested_addenda) &&
            data.suggested_addenda.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      style={{ verticalAlign: "middle", marginRight: "8px" }}
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Suggested Addenda</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.suggested_addenda.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.suggested_addenda.map(
                    (addendum: any, index: number) => (
                      <Box
                        key={`addendum-${index}`}
                        sx={{
                          backgroundColor: "#f3fdff",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "12px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                            borderBottom: "2px solid #3095AB",
                            paddingBottom: "8px",
                            marginBottom: "12px",
                          }}
                        >
                          {addendum.section || "Section"}
                        </Typography>
                        <Typography sx={{ fontSize: "14px", marginTop: "4px" }}>
                          <strong>Suggested Text:</strong>{" "}
                          {addendum.addendum_text || "N/A"}
                        </Typography>

                        {addendum.regulation_link && (
                          <Box
                            sx={{
                              marginTop: "8px",
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <Typography sx={{ fontSize: "14px" }}>
                              <strong>Regulation Reference:</strong>
                            </Typography>
                            <Link
                              href={addendum.regulation_link}
                              target="_blank"
                              rel="noopener"
                              sx={{
                                fontSize: "13px",
                                display: "inline-flex",
                                gap: "4px",
                              }}
                            >
                              View regulation
                            </Link>
                          </Box>
                        )}

                        {addendum.example_paragraph && (
                          <Box
                            sx={{
                              display: "flex",
                              padding: "8px",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              gap: "12px",
                              alignSelf: "stretch",
                              borderRadius: "8px",
                              border: "1px solid #e5e7eb",
                              background: "#ffffff",
                              marginTop: "8px",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontWeight: 600,
                                color: "#262626",
                                borderBottom: "1px solid #e5e7eb",
                                paddingBottom: "4px",
                                width: "100%",
                              }}
                            >
                              Implementation Example:
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "14px",
                                fontStyle: "italic",
                              }}
                            >
                              {addendum.example_paragraph}
                            </Typography>
                          </Box>
                        )}

                        {addendum.rationale && (
                          <Typography
                            sx={{
                              fontSize: "14px",
                              marginTop: "4px",
                              borderTop: "2px solid #3095AB",
                              paddingTop: "8px",
                            }}
                          >
                            <strong>Rationale:</strong> {addendum.rationale}
                          </Typography>
                        )}
                      </Box>
                    ),
                  )}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Risk Ratings Summary Section */}
          {Array.isArray(data.risk_ratings_summary) &&
            data.risk_ratings_summary.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      style={{ verticalAlign: "middle", marginRight: "8px" }}
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Risk Ratings Summary</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.risk_ratings_summary.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  {data.risk_ratings_summary.map((risk: any, index: number) => (
                    <Box
                      key={`risk-summary-${index}`}
                      sx={{
                        backgroundColor: "#f3fdff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "12px",
                        marginBottom: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: "2px solid #3095AB",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "15px",
                            fontWeight: 600,
                          }}
                        >
                          {risk.category || "Category"}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              padding: "5px",
                              flexDirection: "column",
                              alignItems: "flex-start",
                              borderRadius: "14px",
                              border:
                                "1px solid var(--Add-on-colors-Storage-Primary, #C48029)",
                              background:
                                "var(--Add-on-colors-Storage-Background, #FFF3E4)",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",
                                fontWeight: 600,
                                textDecoration: "none",
                              }}
                            >
                              {risk.compliance_risk || "N/A"} / Risk
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              padding: "4px 12px",
                              justifyContent: "center",
                              alignItems: "center",
                              gap: "4px",
                              border: "1px solid #0A829D",
                              background: "#fff",
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "15px",
                                fontWeight: 600,
                              }}
                            >
                              {risk.risk_score ??
                                (risk.readiness_score !== undefined &&
                                risk.readiness_score !== null
                                  ? 100 - Number(risk.readiness_score)
                                  : "N/A")}{" "}
                              / 100
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                      {risk.document_sections_analyzed && (
                        <Typography sx={{ fontSize: "14px", marginTop: "4px" }}>
                          <strong>Document Section Analyzed:</strong>{" "}
                          {risk.document_sections_analyzed.join(", ")}
                        </Typography>
                      )}
                      {risk.regulatory_sections_added && (
                        <Typography sx={{ fontSize: "14px", marginTop: "4px" }}>
                          <strong>Missing Regulatory Requirement:</strong>{" "}
                          {risk.regulatory_sections_added.join(", ")}
                        </Typography>
                      )}
                      {risk.analysis_basis && (
                        <Box
                          sx={{
                            display: "flex",
                            padding: "8px",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "12px",
                            alignSelf: "stretch",
                            borderRadius: "8px",
                            border: "1px solid #e5e7eb",
                            background: "#ffffff",
                            marginTop: "8px",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: 600,
                              color: "#262626",
                              borderBottom: "1px solid #e5e7eb",
                              paddingBottom: "4px",
                              width: "100%",
                            }}
                          >
                            Analysis Basic:
                          </Typography>
                          <Typography sx={{ fontSize: "14px" }}>
                            {risk.analysis_basis}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Final Recommendations Section */}
          {Array.isArray(data.final_recommendations) &&
            data.final_recommendations.length > 0 && (
              <StyledAccordion defaultExpanded={false}>
                <AccordionSummary
                  expandIcon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 7.5L10 12.5L15 7.5"
                        stroke="#262626"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  }
                  sx={{
                    minHeight: "45px",
                    borderRadius: "4px",
                    border: "1px solid #9EE5F4",
                    backgroundColor: "#FFF",
                    boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    "&.Mui-expanded": {
                      minHeight: "45px",
                      border: "1px solid #9EE5F4",
                      boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                    },
                    "& .MuiAccordionSummary-content": {
                      margin: "7px 0",
                      flex: "1 1 auto",
                      width: "100%",
                      minWidth: 0,
                      "&.Mui-expanded": {
                        margin: "7px 0",
                      },
                    },
                    "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                      transform: "rotate(180deg)",
                    },
                  }}
                >
                  <AccordionTitle component="div" className="accordion-title">
                    <AccordionTitleLeading>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                    >
                      <path
                        d="M5 5C5 3.89543 5.89543 3 7 3H10.5858C10.9836 3 11.3651 3.15804 11.6464 3.43934L14.5607 6.35355C14.842 6.63486 15 7.01639 15 7.41421V15C15 16.1046 14.1046 17 13 17H7C5.89543 17 5 16.1046 5 15V5ZM7 4C6.44772 4 6 4.44772 6 5V15C6 15.5523 6.44772 16 7 16H13C13.5523 16 14 15.5523 14 15V8H11.5C10.6716 8 10 7.32843 10 6.5V4H7ZM11.5 7H13.7929L11 4.20711V6.5C11 6.77614 11.2239 7 11.5 7ZM7.5 10C7.22386 10 7 10.2239 7 10.5C7 10.7761 7.22386 11 7.5 11H12.5C12.7761 11 13 10.7761 13 10.5C13 10.2239 12.7761 10 12.5 10H7.5ZM7 12.5C7 12.2239 7.22386 12 7.5 12H12.5C12.7761 12 13 12.2239 13 12.5C13 12.7761 12.7761 13 12.5 13H7.5C7.22386 13 7 12.7761 7 12.5ZM7.5 14C7.22386 14 7 14.2239 7 14.5C7 14.7761 7.22386 15 7.5 15H12.5C12.7761 15 13 14.7761 13 14.5C13 14.2239 12.7761 14 12.5 14H7.5Z"
                        fill="#065B70"
                      />
                    </svg>
                    <span>Final Recommendations</span>
                  </AccordionTitleLeading>
                    <AccordionCountBadge className="accordion-count-badge">
                      {data.final_recommendations.length} items
                    </AccordionCountBadge>
                  </AccordionTitle>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    padding: "16px",
                    backgroundColor: "#ffffff",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "8px",
                    }}
                  >
                    {data.final_recommendations.map(
                      (recommendation: any, index: number) => (
                        <Box
                          key={`final-rec-${index}`}
                          sx={{
                            backgroundColor: "#f3fdff",
                            border: "1px solid #e5e7eb",
                            borderRadius: "4px",
                            padding: "12px",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              borderBottom: "2px solid #3095AB",
                              paddingBottom: "8px",
                              marginBottom: "8px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                padding: "4px 16px",
                                flexDirection: "column",
                                alignItems: "flex-start",
                                gap: "8px",
                                ...(recommendation.priority === "Immediate" && {
                                  borderRadius: "16px",
                                  border: "1px solid #A4262C",
                                  background: "#FDE7E9",
                                }),
                                ...(recommendation.priority === "Medium" && {
                                  borderRadius: "16px",
                                  border: "1px solid #FABC89",
                                  background: "#FFF",
                                }),
                                ...(recommendation.priority === "High" && {
                                  borderRadius: "16px",
                                  border: "1px solid #F46363",
                                  background: "#FFF",
                                }),
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  fontWeight: 600,
                                  ...(recommendation.priority ===
                                    "Immediate" && {
                                    color: "#D13438",
                                  }),
                                  ...(recommendation.priority === "Medium" && {
                                    color: "#FABC89",
                                  }),
                                  ...(recommendation.priority === "High" && {
                                    color: "#F46363",
                                  }),
                                }}
                              >
                                {recommendation.priority || "N/A"}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                display: "flex",
                                padding: "4px 12px",
                                justifyContent: "center",
                                alignItems: "center",
                                gap: "4px",
                                borderRadius: "16px",
                                border: "1px solid #0A829D",
                                background: "#E8F6F9",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: 600,
                                  color: "#0A829D",
                                }}
                              >
                                {recommendation.timeline || "N/A"}
                              </Typography>
                            </Box>
                          </Box>
                          <Typography
                            sx={{ fontSize: "14px", marginTop: "4px" }}
                          >
                            {recommendation.recommendation || "Recommendation"}
                          </Typography>
                        </Box>
                      ),
                    )}
                  </Box>
                </AccordionDetails>
              </StyledAccordion>
            )}

          {/* Compliance Deficiencies Section */}
          {/* <StyledAccordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                minHeight: "45px",
                borderRadius: "4px",
                border: "1px solid #9EE5F4",
                backgroundColor: "#FFF",
                boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                "&.Mui-expanded": {
                  minHeight: "45px",
                  border: "1px solid #9EE5F4",
                  boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "7px 0",
                  "&.Mui-expanded": {
                    margin: "7px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle className="accordion-title">
                Compliance Deficiencies
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#065b70",
                  marginBottom: "16px",
                }}
              >
                Based on CMS Conditions of Participation (CoPs), Joint
                Commission, OSHA, and HIPAA (where relevant):
              </Typography>

              {Array.isArray(data.compliance_deficiencies) &&
                data.compliance_deficiencies.map(
                  (deficiency: any, index: number) => (
                    <Box
                      key={index}
                      sx={{
                        backgroundColor: "#f3fdff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "4px",
                        padding: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Box
                        sx={{
                          borderBottom: "1px solid #737373",
                          paddingBottom: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#262626",
                          }}
                        >
                          {deficiency.category} - Risk Level:{" "}
                          {deficiency.risk_level}
                        </Typography>
                      </Box>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Requirement:</strong> {deficiency.requirement}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Source:</strong> {deficiency.regulatory_source}{" "}
                        - {deficiency.specific_regulation_citation}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                          marginBottom: "8px",
                        }}
                      >
                        <strong>Deficiency:</strong>{" "}
                        {deficiency.deficiency_description}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Associated Risks:</strong>{" "}
                        {deficiency.associated_risks?.join(", ")}
                      </Typography>
                    </Box>
                  )
                )}
            </AccordionDetails>
          </StyledAccordion> */}

          {/* Potential Fines & Penalties Section */}
          {/* <StyledAccordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                minHeight: "56px",
                "&.Mui-expanded": {
                  minHeight: "56px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                  "&.Mui-expanded": {
                    margin: "12px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle className="accordion-title">
                Potential Fines & Penalties if Not Corrected
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Box
                sx={{
                  backgroundColor: "#f3fdff",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                {data.potential_penalties?.penalty_table?.map(
                  (penalty: any, index: number) => (
                    <Box key={index} sx={{ marginBottom: "8px" }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Regulator:</strong>{" "}
                        {penalty?.regulator ?? "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Fine Range:</strong>{" "}
                        {penalty?.fine_range ?? "N/A"}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#262626",
                          lineHeight: 1.5,
                        }}
                      >
                        <strong>Estimated Exposure:</strong>{" "}
                        {penalty?.estimated_exposure ?? "N/A"}
                      </Typography>
                      {penalty?.notes ? (
                        <Typography
                          sx={{
                            fontSize: "14px",
                            color: "#262626",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Notes:</strong> {penalty.notes}
                        </Typography>
                      ) : null}
                    </Box>
                  )
                )}

                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                    marginTop: "12px",
                    fontWeight: 600,
                  }}
                >
                  <strong>Estimated Financial Impact:</strong>
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                    marginBottom: "4px",
                  }}
                >
                  • Penalty Range:{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_penalty_exposure?.min || "N/A"}{" "}
                  -{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_penalty_exposure?.max || "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#262626",
                    lineHeight: 1.5,
                  }}
                >
                  • Corrective Investment:{" "}
                  {data.potential_penalties?.penalty_charts
                    ?.estimated_corrective_investment?.total || "N/A"}
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion> */}

          {/* Plan of Correction Section */}
          {/* <StyledAccordion defaultExpanded={false}>
            <AccordionSummary
              expandIcon={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="#262626"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              }
              sx={{
                minHeight: "56px",
                "&.Mui-expanded": {
                  minHeight: "56px",
                },
                "& .MuiAccordionSummary-content": {
                  margin: "12px 0",
                  "&.Mui-expanded": {
                    margin: "12px 0",
                  },
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(180deg)",
                },
              }}
            >
              <AccordionTitle className="accordion-title">
                Plan of Correction (POC)
              </AccordionTitle>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                padding: "16px",
                backgroundColor: "#ffffff",
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#065b70",
                  marginBottom: "16px",
                }}
              >
                To bring the facility into compliance:
              </Typography>

              
              {data.plan_of_correction?.action_table?.map(
                (action: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f3fdff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Box
                      sx={{
                        borderBottom: "1px solid #737373",
                        paddingBottom: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#262626",
                        }}
                      >
                        Action {index + 1}
                      </Typography>
                    </Box>
                    
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Priority:</strong>{" "}
                      {normalizePriority(action.priority)}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Action:</strong> {action.action}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                        marginBottom: "8px",
                      }}
                    >
                      <strong>Timeline:</strong> {action.timeline}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#262626",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Responsible:</strong> {action.responsible}
                    </Typography>
                  </Box>
                )
              )}

             
              {data.plan_of_correction?.documentation_improvements?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    Documentation Improvements
                  </Typography>
                  {data.plan_of_correction.documentation_improvements.map(
                    (doc: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            fontWeight: 600,
                            color: "#065b70",
                            marginBottom: "4px",
                          }}
                        >
                          {doc.improvement}
                        </Typography>
                        {Array.isArray(doc.implementation_steps) &&
                          doc.implementation_steps.length > 0 && (
                            <Typography
                              sx={{
                                fontSize: "13px",
                                color: "#374151",
                                lineHeight: 1.5,
                                marginBottom: "4px",
                              }}
                            >
                              <strong>Implementation Steps:</strong>{" "}
                              {doc.implementation_steps.join(", ")}
                            </Typography>
                          )}
                        {doc.timeline && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Timeline:</strong> {doc.timeline}
                          </Typography>
                        )}
                      </Box>
                    )
                  )}
                </>
              ) : null}

              
              {data.plan_of_correction?.system_improvements?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    System Improvements
                  </Typography>
                  {data.plan_of_correction.system_improvements.map(
                    (sys: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>System:</strong> {sys.system}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Improvement:</strong> {sys.improvement}
                        </Typography>
                        {sys.implementation_cost && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                              marginBottom: "4px",
                            }}
                          >
                            <strong>Implementation Cost:</strong>{" "}
                            {sys.implementation_cost}
                          </Typography>
                        )}
                        {sys.timeline && (
                          <Typography
                            sx={{
                              fontSize: "13px",
                              color: "#374151",
                              lineHeight: 1.5,
                            }}
                          >
                            <strong>Timeline:</strong> {sys.timeline}
                          </Typography>
                        )}
                      </Box>
                    )
                  )}
                </>
              ) : null}

             
              {data.plan_of_correction?.monitoring_tracking?.length ? (
                <>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#065b70",
                      marginTop: "16px",
                      marginBottom: "12px",
                    }}
                  >
                    Monitoring & Tracking
                  </Typography>
                  {data.plan_of_correction.monitoring_tracking.map(
                    (m: any, idx: number) => (
                      <Box
                        key={idx}
                        sx={{
                          backgroundColor: "#f8f9fa",
                          border: "1px solid #e5e7eb",
                          borderRadius: "4px",
                          padding: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Metric:</strong> {m.metric}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Frequency:</strong> {m.frequency}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                            marginBottom: "4px",
                          }}
                        >
                          <strong>Responsible:</strong> {m.responsible_party}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#374151",
                            lineHeight: 1.5,
                          }}
                        >
                          <strong>Reporting Method:</strong>{" "}
                          {m.reporting_method}
                        </Typography>
                      </Box>
                    )
                  )}
                </>
              ) : null}

             
              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#065b70",
                  marginTop: "16px",
                  marginBottom: "12px",
                }}
              >
                Training Requirements:
              </Typography>
              {data.plan_of_correction?.training_requirements?.map(
                (training: any, index: number) => (
                  <Box
                    key={index}
                    sx={{
                      backgroundColor: "#f8f9fa",
                      border: "1px solid #e5e7eb",
                      borderRadius: "4px",
                      padding: "8px",
                      marginBottom: "8px",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#065b70",
                        marginBottom: "4px",
                      }}
                    >
                      {training.training_topic}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        color: "#374151",
                        lineHeight: 1.5,
                      }}
                    >
                      <strong>Target Audience:</strong>{" "}
                      {training.target_audience}
                      <br />
                      <strong>Frequency:</strong> {training.frequency}
                      <br />
                      <strong>Duration:</strong> {training.duration}
                    </Typography>
                  </Box>
                )
              )}

              <Typography
                sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#065b70",
                  marginTop: "16px",
                  marginBottom: "12px",
                }}
              >
                Estimated Corrective Costs:
              </Typography>
              <Box
                sx={{
                  backgroundColor: "#f8f9fa",
                  border: "1px solid #e5e7eb",
                  borderRadius: "4px",
                  padding: "8px",
                }}
              >
                <Typography
                  sx={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}
                >
                  <strong>Immediate Costs:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs?.immediate_costs ||
                    "N/A"}
                  <br />
                  <strong>Long-term Costs:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs?.long_term_costs ||
                    "N/A"}
                  <br />
                  <strong>Total Estimated Cost:</strong>{" "}
                  {data.plan_of_correction?.corrective_costs
                    ?.total_estimated_cost || "N/A"}
                </Typography>
              </Box>
            </AccordionDetails>
          </StyledAccordion> */}
        </Box>
      </ReportDetailsContainer>
    );
  };

  /**
   * Renders a separate accordion for AffableBPM file analysis
   * NOTE: This is now disabled - analysis is shown in the same accordion via renderAffableSyncPanel
   */
  const renderAffableFileAnalysisAccordion = () => {
    // Disable separate accordion - analysis now shows in the same accordion
    return null;
  };

  const renderRecentlyReviewedDocuments = () => {
    // Map document types
    const typeToDocTypeMap: Record<string, "policy" | "emergency" | "survey"> =
      {
        "Policy and Procedure Review": "policy",
        "Emergency Plan Review": "emergency",
        "Getting Ready for Survey": "survey",
      };

    // Get file name from data_obj.metadata or generate from type and date
    const getFileName = (historyItem: any) => {
      const metadata = historyItem?.data_obj?.metadata;
      if (metadata?.filename) {
        return metadata.filename;
      }
      // Generate filename from type and date
      const date = historyItem?.created_at
        ? new Date(historyItem.created_at).toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          })
        : new Date().toLocaleDateString("en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
          });
      const typeName = historyItem.type?.replace(/\s+/g, "-") || "Document";
      return `${typeName}-${date}.pdf`;
    };

    // Get file size from data_obj.metadata or default
    const getFileSize = (historyItem: any) => {
      const metadata = historyItem?.data_obj?.metadata;
      if (metadata?.file_size) {
        return formatFileSize(metadata.file_size);
      }
      return "N/A";
    };

    // Get file extension
    const getFileExtension = (fileName: string) => {
      const ext = fileName.split(".").pop()?.toLowerCase() || "pdf";
      return ext;
    };

    // Handle view analysis click - show the report inside the Recently Reviewed
    // section (do NOT reuse the upload accordions).
    const handleViewAnalysis = (historyItem: any) => {
      const docType = typeToDocTypeMap[historyItem.type];
      if (!docType) return;

      const analysisData = historyItem?.data_obj?.analysis;
      if (!analysisData) return;

      // Wrap the analysis in the expected ContinuousComplianceResponse format
      const analysis: ContinuousComplianceResponse = {
        success: true,
        message: "Analysis loaded from history",
        analysis: analysisData,
        metadata: historyItem?.data_obj?.metadata || {},
      };

      // Store in dedicated history state so we can render the report
      // inside the Recently Reviewed section itself.
      setHistorySelectedAnalysis(analysis);
      setHistorySelectedDocType(docType);
      setHistorySelectedId(historyItem?.id ?? null);
    };

    if (loadingHistory) {
      return (
        <StyledAccordion
          expanded={tabExpandedPanel === "recentlyReviewed"}
          onChange={handleTabAccordionChange("recentlyReviewed")}
        >
          <AccordionSummary>
            <AccordionTitle className="accordion-title">
              Recently Reviewed Documents
            </AccordionTitle>
          </AccordionSummary>
          <AccordionDetails>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                padding: "20px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#065B70" }} />
            </Box>
          </AccordionDetails>
        </StyledAccordion>
      );
    }

    return (
      <StyledAccordion
        expanded={tabExpandedPanel === "recentlyReviewed"}
        onChange={handleTabAccordionChange("recentlyReviewed")}
      >
        <AccordionSummary
          expandIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#262626"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          aria-controls="recently-reviewed-content"
          id="recently-reviewed-header"
          sx={{
            alignItems: "center",
            borderRadius: "4px",
            border: "1px solid #9EE5F4",
            backgroundColor: "#FFF",
            boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
          }}
        >
          <AccordionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
                fill="#065B70"
              />
            </svg>
          </AccordionIcon>
          <AccordionTitle className="accordion-title">
            Recently Reviewed Documents
          </AccordionTitle>
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: "#ffffff",
            padding: "12px",
            // StyledAccordion zeros details paddingTop; restore so nested section headers
            // (e.g. Policy & Procedure Review) are not flush under the parent summary.
            paddingTop: "16px !important",
            display: "flex",
            flexDirection: "column",
            gap: "12px",
          }}
        >
          {(() => {
            // Group documents by type
            const documentTypes = [
              {
                type: "Policy and Procedure Review",
                label: "Policy & Procedure Review",
              },
              { type: "Emergency Plan Review", label: "Emergency Plan Review" },
              {
                type: "Getting Ready for Survey",
                label: "Getting Ready for Survey",
              },
            ];

            if (analysisHistory.length === 0) {
              return (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#737373",
                    fontFamily: "Rethink Sans, sans-serif",
                    padding: "8px",
                    textAlign: "center",
                  }}
                >
                  No documents reviewed yet
                </Typography>
              );
            }

            const groupedHistory = documentTypes
              .map((docType) => ({
                ...docType,
                documents: analysisHistory.filter(
                  (item) => item.type === docType.type,
                ),
              }))
              // Only keep sections that actually have documents
              .filter((section) => section.documents.length > 0);

            const sections = groupedHistory.map((section, sectionIndex) => {
              const hasDocuments = section.documents.length > 0;

              // Per-type pagination state and derived values
              const docKey = typeToDocTypeMap[section.type] as
                | "policy"
                | "emergency"
                | "survey"
                | undefined;

              let page = 1;
              let rowsPerPage = 5;
              let setPage: (v: number) => void = () => {};
              let setRowsPerPage: (v: number) => void = () => {};
              let totalItems = 0;

              if (docKey === "policy") {
                page = historyPagePolicy;
                rowsPerPage = historyRowsPerPagePolicy;
                setPage = setHistoryPagePolicy;
                setRowsPerPage = setHistoryRowsPerPagePolicy;
                totalItems = historyTotalPolicy;
              } else if (docKey === "emergency") {
                page = historyPageEmergency;
                rowsPerPage = historyRowsPerPageEmergency;
                setPage = setHistoryPageEmergency;
                setRowsPerPage = setHistoryRowsPerPageEmergency;
                totalItems = historyTotalEmergency;
              } else if (docKey === "survey") {
                page = historyPageSurvey;
                rowsPerPage = historyRowsPerPageSurvey;
                setPage = setHistoryPageSurvey;
                setRowsPerPage = setHistoryRowsPerPageSurvey;
                totalItems = historyTotalSurvey;
              }

              const totalPages =
                totalItems > 0 ? Math.ceil(totalItems / rowsPerPage) : 1;
              const currentPage = Math.min(page, totalPages);
              // Data is already server-side paginated, no client-side slicing needed
              const pageDocuments = section.documents;

              const isExpanded =
                docKey && historyExpandedTypes[docKey] !== undefined
                  ? historyExpandedTypes[docKey]
                  : true;

              return (
                <Box
                  key={section.type}
                  sx={{ display: "flex", flexDirection: "column" }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "10px 12px",
                      borderRadius: isExpanded ? "4px 4px 0 0" : "4px",
                      // Match primary teal header style used elsewhere
                      backgroundColor: hasDocuments ? "#0A829D" : "#F5F5F5",
                      border: hasDocuments
                        ? "1px solid #0A829D"
                        : "1px solid #E0E0E0",
                      cursor: hasDocuments ? "pointer" : "default",
                      opacity: hasDocuments ? 1 : 0.6,
                    }}
                    onClick={() => {
                      if (!docKey || !hasDocuments) return;
                      setHistoryExpandedTypes((prev) => ({
                        ...prev,
                        [docKey]: !prev[docKey],
                      }));
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: hasDocuments ? "#FFFFFF" : "#737373",
                        fontFamily: "Rethink Sans, sans-serif",
                      }}
                    >
                      {section.label}
                    </Typography>
                    {hasDocuments && (
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        style={{
                          transform: isExpanded
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transition: "transform 0.2s ease",
                        }}
                      >
                        <path
                          d="M5 7.5L10 12.5L15 7.5"
                          stroke={hasDocuments ? "#FFFFFF" : "#065B70"}
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </Box>

                  {hasDocuments && isExpanded && (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "8px",
                        border: "1px solid #99d4e1",
                        borderTop: "none",
                        borderRadius: "0 0 8px 8px",
                        padding: "12px",
                      }}
                    >
                      {pageDocuments.map((historyItem, index) => {
                        const fileName = getFileName(historyItem);
                        const fileSize = getFileSize(historyItem);
                        const fileExt = getFileExtension(fileName);
                        const analysis = historyItem?.data_obj?.analysis;
                        const isSelected =
                          historySelectedId !== null &&
                          historySelectedId === historyItem.id;

                        return (
                          <React.Fragment key={historyItem.id || index}>
                            <FileDisplayContainer>
                              <FileInfoContainer>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    minWidth: "24px",
                                    flexShrink: 0,
                                  }}
                                >
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: "16px",
                                      fontWeight: 700,
                                      color: "#065B70",
                                      fontFamily: "Rethink Sans, sans-serif",
                                    }}
                                  >
                                    {index + 1}
                                  </Typography>
                                </Box>
                                <FileIcon>
                                  <ComplianceHistoryFileGlyph />
                                </FileIcon>
                                <FileDetails>
                                  <FileName
                                    sx={{
                                      fontSize: "12px",
                                      fontWeight: 700,
                                      color: "#262626",
                                    }}
                                  >
                                    {fileName}
                                  </FileName>
                                  <FileSize>
                                    {fileExt} | {fileSize}
                                    {historyItem?.created_at && (
                                      <>
                                        {" "}
                                        |{" "}
                                        <span style={{ textTransform: "none" }}>
                                          Uploaded:{" "}
                                          {formatClientUploadTimestamp(
                                            new Date(historyItem.created_at),
                                          )}
                                        </span>
                                      </>
                                    )}
                                  </FileSize>
                                </FileDetails>
                              </FileInfoContainer>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: "8px",
                                  alignItems: "center",
                                }}
                              >
                                {analysis && (
                                  <>
                                    {/* Delete first, then View Analysis, to match design */}
                                    <DeleteButton
                                      aria-label="Delete recently reviewed document"
                                      onClick={async (event) => {
                                        // Prevent parent row click (accordion toggle)
                                        event.stopPropagation();
                                        if (!historyItem.id) return;
                                        try {
                                          await continuousComplianceService.deleteAnalysisHistoryEntry(
                                            historyItem.id,
                                          );
                                          // Remove from local state
                                          setAnalysisHistory((prev) =>
                                            prev.filter(
                                              (item) =>
                                                item.id !== historyItem.id,
                                            ),
                                          );
                                          // Clear selected state if this was selected
                                          if (
                                            historySelectedId === historyItem.id
                                          ) {
                                            setHistorySelectedAnalysis(null);
                                            setHistorySelectedDocType(null);
                                            setHistorySelectedId(null);
                                          }
                                          toast.success(
                                            "Recently reviewed document removed successfully.",
                                          );
                                        } catch (error) {
                                          console.error(
                                            "Failed to delete analysis history entry:",
                                            error,
                                          );
                                          toast.error(
                                            "Failed to delete recently reviewed document. Please try again.",
                                          );
                                        }
                                      }}
                                    >
                                      <DeleteFileIcon />
                                    </DeleteButton>

                                    <ViewReportButton
                                      onClick={() => {
                                        if (isSelected) {
                                          setHistorySelectedAnalysis(null);
                                          setHistorySelectedDocType(null);
                                          setHistorySelectedId(null);
                                        } else {
                                          handleViewAnalysis(historyItem);
                                        }
                                      }}
                                    >
                                      <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="none"
                                        aria-hidden
                                      >
                                        <path
                                          d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                                          fill="white"
                                        />
                                      </svg>
                                      {isSelected
                                        ? "Hide Report"
                                        : "View Analysis"}
                                    </ViewReportButton>
                                  </>
                                )}
                              </Box>
                            </FileDisplayContainer>

                            {historySelectedAnalysis &&
                              historySelectedId === historyItem.id &&
                              historySelectedDocType &&
                              renderReportDetails(
                                historySelectedAnalysis,
                                historySelectedDocType,
                              )}
                          </React.Fragment>
                        );
                      })}

                      {/* Section-level pagination row at bottom (per type) */}
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "flex-end",
                          alignItems: "center",
                          mt: 1,
                          gap: 1,
                        }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              color: "#4b5563",
                              fontFamily: "Rethink Sans, sans-serif",
                            }}
                          >
                            Rows per page
                          </Typography>
                          <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                              const value = Number(e.target.value) || 5;
                              setRowsPerPage(value);
                              setPage(1);
                            }}
                            size="small"
                            sx={{
                              fontSize: 12,
                              height: 28,
                              "& .MuiSelect-select": {
                                padding: "4px 8px",
                              },
                            }}
                          >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={20}>20</MenuItem>
                          </Select>
                        </Box>
                        <Pagination
                          count={totalPages}
                          page={currentPage}
                          onChange={(_, value) => setPage(value)}
                          size="small"
                          sx={{
                            "& .MuiPaginationItem-root": {
                              fontSize: 12,
                              height: 28,
                              minWidth: 28,
                              padding: "0 6px",
                            },
                            "& .MuiPaginationItem-root.Mui-selected": {
                              backgroundColor: "#0a829d",
                              color: "#ffffff",
                            },
                            "& .MuiPaginationItem-root.Mui-selected:hover": {
                              backgroundColor: "#0a829d",
                            },
                          }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
              );
            });

            return sections;
          })()}
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  const renderUploadAccordion = (title: string) => {
    // SSOT-76 / #10192: policy-only upload accordion (emergency/survey stay unmounted).
    const type = "policy" as const;
    const file = policyFile;
    const loading = loadingPolicy;
    const error = errorPolicy;
    const analysis = policyAnalysis;
    const isDragOver = dragOverPolicy;

    return (
      <StyledAccordion
        expanded={tabExpandedPanel === type}
        onChange={handleTabAccordionChange(type)}
      >
        <AccordionSummary
          expandIcon={
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M5 7.5L10 12.5L15 7.5"
                stroke="#262626"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          aria-controls={`${type}-content`}
          id={`${type}-header`}
          sx={{
            alignItems: "center",
            borderRadius: "4px",
            border: "1px solid #9EE5F4",
            backgroundColor: "#FFF",
            boxShadow: "0 4px 8px 0 rgba(111, 211, 232, 0.15)",
            "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
              transform: "rotate(180deg)",
            },
          }}
        >
          <AccordionIcon>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
            >
              <path
                d="M15 12.5854C15.5826 12.7913 16 13.3469 16 14V14.5C16 15.6046 15.1046 16.5 14 16.5H6C4.89543 16.5 4 15.6046 4 14.5V9C4 8.34689 4.4174 7.79127 5 7.58535V6C5 5.17157 5.67157 4.5 6.5 4.5H11.5C12.3284 4.5 13 5.17157 13 6V12.5H14L13.9997 6.58525C14.5825 6.79109 15 7.34678 15 8V12.5854ZM12 11.7929V6C12 5.72386 11.7761 5.5 11.5 5.5H6.5C6.22386 5.5 6 5.72386 6 6V7.5H7.08579C7.48361 7.5 7.86514 7.65804 8.14645 7.93934L12 11.7929ZM5 9V14.5C5 15.0523 5.44772 15.5 6 15.5H14C14.5523 15.5 15 15.0523 15 14.5V14C15 13.7239 14.7761 13.5 14.5 13.5H12.9142C12.5164 13.5 12.1349 13.342 11.8536 13.0607L7.43934 8.64645C7.34557 8.55268 7.21839 8.5 7.08579 8.5H5.5C5.22386 8.5 5 8.72386 5 9Z"
                fill="#065B70"
              />
            </svg>
          </AccordionIcon>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 1,
              flex: 1,
              minWidth: 0,
            }}
          >
            <AccordionTitle
              className="accordion-title"
              sx={{ lineHeight: 1.25 }}
            >
              {title}
            </AccordionTitle>
          </Box>
          {isAffableConnected && (
            <Button
              variant="outlined"
              disabled={isConnectingAffable}
              onClick={(e) => {
                e.stopPropagation();
                console.log(
                  "🎯 Connect with AffableFLOW clicked - from accordion",
                );
                setAffableSourceAccordion("policy");
                handleConnectAffableBPM();
              }}
              sx={{
                display: "flex",
                padding: "4px 12px",
                justifyContent: "center",
                alignItems: "center",
                gap: "6px",
                borderRadius: "8px",
                minHeight: "32px",
                height: "32px",
                marginRight: "8px",
                border: "1px solid var(--Primary-Color-Palette-400, #5BBFD6)",
                background:
                  "var(--Brand-Colors-Brand-Light-Bacground, #E6FAFC)",
                whiteSpace: "nowrap",
                "&:hover": {
                  background: "#f5fdff",
                  borderColor: "#065b70",
                  boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
                },
                "&:active": {
                  background: "#e5f9fd",
                },
              }}
            >
              <AffableConnectedIcon />
              <Typography
                sx={{
                  color: "var(--Brand-Colors-color-brand-hover, #086E86)",
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "13px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "normal",
                  textTransform: "none",
                }}
              >
                {isConnectingAffable
                  ? "Connecting..."
                  : "Connect with AffableFLOW"}
              </Typography>
            </Button>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            backgroundColor: "#ffffff",
            padding: "12px",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "5px",
            }}
          >
            <DescriptionText>We offer a full policy review to ensure current compliance standards based on your facility's regulations, surveyor feedback and expertise.</DescriptionText>

            <UploadContainer sx={{ marginTop: "8px" }}>
              {loading || isSyncingAffableRecords ? (
                <Box
                  sx={{
                    textAlign: "center",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                    padding: "32px",
                  }}
                >
                  <CircularProgress size={24} sx={{ color: "#065b70" }} />
                  {!isSyncingAffableRecords && (
                    <>
                      <Typography
                        sx={{
                          fontSize: "17px",
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Analyzing document…
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "15px",
                          color: "#737373",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        This AI-assisted analysis may take a few minutes.
                      </Typography>
                    </>
                  )}
                </Box>
              ) : file && !analysis ? (
                <FileDisplayContainer
                  sx={{
                    flexDirection: showPolicyReport ? "column" : "row",
                    width: "calc(100% - 32px)",
                  }}
                >
                  <FileInfoContainer>
                    <FileIcon>
                      <ComplianceHistoryFileGlyph />
                    </FileIcon>
                    <FileDetails>
                      <FileName>{file.name}</FileName>
                      <FileSize>
                        {(file.name.split(".").pop() || "").toLowerCase()} |{" "}
                        {formatFileSize(file.size)}
                        {policyUploadDate && (
                          <>
                            {" "}
                            |{" "}
                            <span style={{ textTransform: "none" }}>
                              Uploaded: {policyUploadDate}
                            </span>
                          </>
                        )}
                      </FileSize>
                    </FileDetails>
                  </FileInfoContainer>
                  <Box
                    sx={{
                      display: "flex",
                      gap: "8px",
                      flexWrap: "wrap",
                      width: showPolicyReport ? "calc(100% - 32px)" : "auto",
                      justifyContent: showPolicyReport
                        ? "flex-end"
                        : "flex-start",
                      marginTop: showPolicyReport ? "8px" : 0,
                    }}
                  >
                    <ReUploadButton
                      onClick={() => {
                        const input = document.getElementById(
                          `${type}-file-input`,
                        ) as HTMLInputElement | null;
                        if (input) {
                          // Reset the value so selecting the same file triggers onChange
                          input.value = "";
                          input.click();
                        }
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                          fill="#065B70"
                        />
                        <path
                          d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                          fill="#065B70"
                        />
                      </svg>
                      Re-Upload
                    </ReUploadButton>
                    <DeleteButton
                      aria-label="Delete uploaded file"
                      onClick={() => handleDeleteFile(type)}
                    >
                      <DeleteFileIcon />
                    </DeleteButton>
                  </Box>
                </FileDisplayContainer>
              ) : !file && !shouldShowAffablePanel ? (
                <UploadArea
                  $isDragOver={isDragOver}
                  onDrop={(e) => handleDrop(e, type)}
                  onDragOver={handleDragOver}
                  onDragEnter={(e) => handleDragEnter(e, type)}
                  onDragLeave={(e) => handleDragLeave(e, type)}
                  onClick={() => {
                    const input = document.getElementById(
                      `${type}-file-input`,
                    ) as HTMLInputElement;
                    input?.click();
                  }}
                  sx={{
                    cursor: "pointer",
                    opacity: 1,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      width: "100%",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "12px",
                        width: "100%",
                        justifyContent: "center",
                      }}
                    >
                      {isAffableConnected && (
                        <>
                          <IconButton
                            aria-label={
                              isSyncingAffableRecords
                                ? "Uploading file with AffableFLOW..."
                                : "Upload file with AffableFLOW"
                            }
                            title={
                              isSyncingAffableRecords
                                ? "Uploading file with AffableFLOW..."
                                : "Upload file with AffableFLOW"
                            }
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAffableRecordsSync(0);
                            }}
                            disabled={isSyncingAffableRecords}
                            sx={{
                              borderRadius: "8px",
                              border: "1px solid #5bbfd6",
                              background: "#e6fafc",
                              padding: "8px 16px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              gap: "8px",
                              "&:hover": {
                                backgroundColor: "#d1f5f8",
                              },
                              "&:active": {
                                backgroundColor: "#c1eff5",
                              },
                            }}
                          >
                            {isSyncingAffableRecords ? (
                              <CircularProgress
                                size={20}
                                sx={{ color: "#065B70" }}
                              />
                            ) : (
                              <>
                                <AffableConnectedIcon />
                                <Typography
                                  sx={{
                                    fontFamily: '"Rethink Sans", sans-serif',
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    color: "#065B70",
                                    textTransform: "none",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Upload file from AffableFLOW
                                </Typography>
                              </>
                            )}
                          </IconButton>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0px",
                            }}
                          >
                            <Box
                              sx={{
                                width: "20px",
                                height: "1px",
                                backgroundColor: "#0E9ABA",
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: "#0E9ABA",
                                fontFamily: "Rethink Sans, sans-serif",
                                padding: "0 2px",
                              }}
                            >
                              OR
                            </Typography>
                            <Box
                              sx={{
                                width: "20px",
                                height: "1px",
                                backgroundColor: "#0E9ABA",
                              }}
                            />
                          </Box>
                        </>
                      )}
                      <UploadButton>
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M3.5 2C3.22386 2 3 2.22386 3 2.5C3 2.77614 3.22386 3 3.5 3H12.5C12.7761 3 13 2.77614 13 2.5C13 2.22386 12.7761 2 12.5 2H3.5ZM8.35355 4.14645C8.15829 3.95118 7.84171 3.95118 7.64645 4.14645L4.14645 7.64645C3.95118 7.84171 3.95118 8.15829 4.14645 8.35355C4.34171 8.54882 4.65829 8.54882 4.85355 8.35355L7.5 5.70711V13.5C7.5 13.7761 7.72386 14 8 14C8.27614 14 8.5 13.7761 8.5 13.5V5.70711L11.1464 8.35355C11.3417 8.54882 11.6583 8.54882 11.8536 8.35355C12.0488 8.15829 12.0488 7.84171 11.8536 7.64645L8.35355 4.14645Z"
                            fill="#065B70"
                          />
                        </svg>
                        Upload Documents
                      </UploadButton>
                      <span style={{ whiteSpace: "pre-line" }}>
                        <Typography
                          sx={{
                            fontSize: "12px",
                            color: "#737373",
                            fontFamily: "Rethink Sans, sans-serif",
                          }}
                        >
                          {"Supported file types: \n DOC, DOCX"}
                        </Typography>
                      </span>
                    </Box>
                  </Box>
                </UploadArea>
              ) : null}
            </UploadContainer>

            <HiddenInput
              id={`${type}-file-input`}
              type="file"
              // Keep input capabilities broad; user guidance shown below the upload button
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={(e) => {
                const files = e.target.files;
                if (files && files.length > 0) {
                  handleFileUpload(files[0], type);
                }
              }}
            />

            {error && (
              <Alert
                severity="error"
                sx={{ marginTop: "12px", fontSize: "14px" }}
              >
                {error}
              </Alert>
            )}

            {analysis && file && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: "5px",
                    alignItems: "stretch",
                    flexWrap: "wrap",
                    marginTop: "5px",
                  }}
                >
                  <FileDisplayContainer
                    sx={{
                      flex: 1,
                      minWidth: "300px",
                      marginTop: 0,
                      flexDirection: "row",
                    }}
                  >
                    <FileInfoContainer>
                      <FileIcon>
                        <ComplianceHistoryFileGlyph />
                      </FileIcon>
                      <FileDetails>
                        <FileName>{file.name}</FileName>
                        <FileSize>
                          {(file.name.split(".").pop() || "").toLowerCase()} |{" "}
                          {formatFileSize(file.size)}
                          {policyUploadDate && (
                            <>
                              {" "}
                              |{" "}
                              <span style={{ textTransform: "none" }}>
                                Uploaded: {policyUploadDate}
                              </span>
                            </>
                          )}
                        </FileSize>
                      </FileDetails>
                    </FileInfoContainer>
                    <Box
                      sx={{
                        display: "flex",
                        gap: "8px",
                        flexWrap: "wrap",
                      }}
                    >
                      <ReUploadButton
                        onClick={() => {
                          const input = document.getElementById(
                            `${type}-file-input`,
                          ) as HTMLInputElement | null;
                          if (input) {
                            input.value = "";
                            input.click();
                          }
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 20 20"
                          fill="none"
                        >
                          <path
                            d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                            fill="#065B70"
                          />
                          <path
                            d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                            fill="#065B70"
                          />
                        </svg>
                        Re-Upload
                      </ReUploadButton>
                      <DeleteButton
                        aria-label="Delete uploaded file"
                        onClick={() => handleDeleteFile(type)}
                      >
                        <DeleteFileIcon />
                      </DeleteButton>
                      <GlobalAdminLlmPromptButton
                          dialogTitle="Policy & procedure compliance — LLM prompt"
                          promptText={
                            analysis?.llmPromptsBySection?.policy_procedure
                          }
                          wallTimeSec={
                            analysis?.llmInvokeWallSecondsBySection
                              ?.policy_procedure ?? null
                          }
                        />
                      <ViewReportButton onClick={() => toggleReport("policy")}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                        >
                          <path
                            d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                            fill="white"
                          />
                        </svg>
                        {showPolicyReport ? "Hide Report" : "View Analysis"}
                      </ViewReportButton>
                    </Box>
                  </FileDisplayContainer>

                  {/* <SuccessContainer
                    sx={{ flex: 1, minWidth: "300px", marginTop: 0 }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: "8px" }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                      >
                        <path
                          d="M8.68149 2.78544C9.24892 1.73813 10.752 1.73821 11.3193 2.78557L17.8198 14.7865C18.3612 15.786 17.6375 17.0009 16.5009 17.0009H3.4982C2.36147 17.0009 1.63783 15.7858 2.17934 14.7864L8.68149 2.78544ZM10.5 7.5C10.5 7.22386 10.2761 7 10 7C9.72386 7 9.5 7.22386 9.5 7.5V11.5C9.5 11.7761 9.72386 12 10 12C10.2761 12 10.5 11.7761 10.5 11.5V7.5ZM10.75 13.75C10.75 13.3358 10.4142 13 10 13C9.58579 13 9.25 13.3358 9.25 13.75C9.25 14.1642 9.58579 14.5 10 14.5C10.4142 14.5 10.75 14.1642 10.75 13.75Z"
                          fill="#ED6C02"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontSize: "16px",
                          fontWeight: 600,
                          color: "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                        }}
                      >
                        Gaps found
                      </Typography>
                    </Box>
                    <ViewReportButton onClick={() => toggleReport("policy")}>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                          fill="white"
                        />
                      </svg>
                      {showPolicyReport ? "Hide Report" : "View Analysis"}
                    </ViewReportButton>
                  </SuccessContainer> */}
                </Box>

                {/* {policyUploadDate && (
                  <LastUploadedContainer>
                    <LastUploadedText>Last Uploaded</LastUploadedText>
                    <LastUploadedText>{policyUploadDate}</LastUploadedText>
                  </LastUploadedContainer>
                )} */}
              </>
            )}

            {showPolicyReport &&
              analysis &&
              renderReportDetails(analysis, type)}

            {shouldShowAffablePanel && (
              <Box sx={{ marginTop: "12px" }}>
                {renderAffableSyncPanel()}
                {errorAffableFile && (
                  <Alert severity="error" sx={{ fontSize: "12px", mt: 1 }}>
                    {errorAffableFile}
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  const selectedAffableDocument = useMemo(() => {
    if (!affableDocuments.length) return null;
    if (selectedAffableDocumentId === null) return affableDocuments[0];
    return (
      affableDocuments.find(
        (document) => document.id === selectedAffableDocumentId,
      ) || affableDocuments[0]
    );
  }, [affableDocuments, selectedAffableDocumentId]);

  const selectedAffableFolder = useMemo(() => {
    if (!selectedAffableDocument) return null;
    if (selectedAffableFolderId === null)
      return selectedAffableDocument.folders[0] || null;
    return (
      selectedAffableDocument.folders.find(
        (folder) => folder.id === selectedAffableFolderId,
      ) ||
      selectedAffableDocument.folders[0] ||
      null
    );
  }, [selectedAffableDocument, selectedAffableFolderId]);

  const totalAffableDocPages = useMemo(
    () =>
      affableRecordCount
        ? Math.ceil(affableRecordCount / affableRowsPerPage)
        : 1,
    [affableRecordCount, affableRowsPerPage],
  );

  // Dynamically extract all column names from records
  // DocumentID is kept internally for selection, but hidden from the visible table.
  const affableTableColumns = useMemo(() => {
    if (affableRecords.length === 0) return [];

    const excludedKeys = ["RowNum", "DeptID", "DocumentID"]; // Keys to exclude from display
    const allKeys = new Set<string>();

    // Collect all keys from all records
    affableRecords.forEach((record) => {
      Object.keys(record).forEach((key) => {
        if (!excludedKeys.includes(key)) {
          allKeys.add(key);
        }
      });
    });

    // Only show first 5 dynamic columns (sorted) to keep table compact
    const dynamicColumns = Array.from(allKeys).sort().slice(0, 5);
    return dynamicColumns;
  }, [affableRecords]);

  const handleAffableRecordsSync = async (
    pageNumber: number = 0,
    pageSize?: number,
  ) => {
    if (isSyncingAffableRecords) return;
    setHasAttemptedAffableSync(true);

    const facilityName = getFacilityName?.();
    if (!facilityName) {
      setAffableSyncError(
        "Facility name unavailable. Please select a facility first.",
      );
      setAffableDocuments([]);
      setAffableRecordCount(0);
      return;
    }

    // Re-open panel on each sync attempt (e.g. after user dismissed an error
    // and chose "Upload from AffableFLOW" again).
    setIsAffablePanelClosed(false);

    // Prefer org_name from My Facilities API (stored on the resolved facility),
    // fall back to the plain facility name if not present.
    const orgName = (resolvedFacility as any)?.org_name || facilityName;

    // Resolve workflow/department for this org & type=Policy from Cirules.
    // If this lookup fails for any reason, we gracefully fall back to the
    // default DeptId used by AffableBPM.
    let resolvedDeptId: number | undefined;
    try {
      const mapping =
        await affablebpmIntegrationService.getWorkflowMappingByTypeAndOrg({
          orgName,
          type: "Policy",
        });
      if (
        mapping?.success &&
        Array.isArray(mapping.data) &&
        mapping.data.length > 0
      ) {
        const first = mapping.data[0];
        if (
          typeof first?.workflowId === "number" &&
          Number.isFinite(first.workflowId)
        ) {
          resolvedDeptId = first.workflowId;
        }
      }
    } catch (error) {
      // Log but do not block the sync; DeptId will fall back to the default.
      console.error(
        "Failed to resolve workflow mapping for AffableBPM:",
        error,
      );
    }
    setIsSyncingAffableRecords(true);
    setAffableSyncError(null);
    try {
      const response =
        await affablebpmIntegrationService.getRecordAndFilesDetails({
          orgName,
          deptId: resolvedDeptId,
          perPage: pageSize ?? affableRowsPerPage ?? 10,
          pageNumber: pageNumber ?? 0,
        });

      // Surface backend error messages (e.g., "User not found with the provided username.")
      const rawResponse = (response || {}) as RawAffableResponse;
      if (
        typeof rawResponse.Status === "string" &&
        rawResponse.Status.toLowerCase() === "failed"
      ) {
        const backendMessage =
          rawResponse.ErrorMsg ||
          rawResponse.Message ||
          "Failed to fetch records from AffableBPM.";

        setAffableDocuments([]);
        setAffableRecords([]);
        setAffableRecordCount(0);
        setIsAffablePanelClosed(false);
        setAffableSyncError(backendMessage);
        return;
      }

      const { documents, records, totalCount } =
        normalizeAffableResponse(response);
      setAffableDocuments(documents);
      setAffableRecords(records);
      setAffableRecordCount(totalCount);
      setIsAffablePanelClosed(false); // Show panel when sync succeeds
      if (!documents.length) {
        setAffableSyncError(
          "No folders returned from AffableBPM for this facility.",
        );
      }
    } catch (error) {
      console.error("Failed to sync AffableBPM documents:", error);
      setAffableSyncError(
        error instanceof Error
          ? error.message || "Failed to sync with AffableBPM."
          : "Failed to sync with AffableBPM.",
      );
      setAffableDocuments([]);
      setAffableRecordCount(0);
    } finally {
      setIsSyncingAffableRecords(false);
    }
  };

  const getAffableFileKey = (folderId: number, imageId: number) =>
    `${folderId}-${imageId}`;

  const isAffableFileSelected = (folderId: number, imageId: number) =>
    selectedAffableFileKey === getAffableFileKey(folderId, imageId);

  const selectAffableFile = (folderId: number, imageId: number) => {
    const key = getAffableFileKey(folderId, imageId);
    setSelectedAffableFileKey((prev) => (prev === key ? null : key));
  };

  /**
   * Converts a base64 string to a File object
   */
  const base64ToFile = (
    base64: string,
    filename: string,
    mimeType: string,
  ): File => {
    // Remove data URL prefix if present (e.g., "data:application/pdf;base64,")
    const base64Data = base64.includes(",") ? base64.split(",")[1] : base64;

    // Convert base64 to binary
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create Blob and then File
    const blob = new Blob([byteArray], { type: mimeType });
    return new File([blob], filename, { type: mimeType });
  };

  /**
   * Runs continuous compliance analysis for a given file and updates AffableBPM state.
   * Used both when fetching a file from AffableBPM and when uploading a local file.
   */
  const analyzeAffableFileForCompliance = async (file: File) => {
    // Store file separately for AffableBPM analysis
    setAffableFile(file);
    setAffableFileUploadDate(formatClientUploadTimestamp(new Date()));
    setShowAffableFileReport(false);
    setAffableFileAnalysis(null);
    setErrorAffableFile(null);

    try {
      const documentType = "Policy and Procedure";

      const currentFacilityData = getFacilityData();
      const facilityType =
        currentFacilityData?.facility_type_slug ||
        currentFacilityData?.facility_type ||
        "Rural Health Clinic"; // Prefer slug for pattern matching
      const country = getSelectedCountryCode() || "USA";

      const sessionState = sessionStorage.getItem("sessionState") || "";
      const sessionCity = sessionStorage.getItem("sessionCity") || "";
      const state = currentFacilityData?.state
        ? String(currentFacilityData.state).trim()
        : sessionState.trim();
      const city = currentFacilityData?.city
        ? String(currentFacilityData.city).trim()
        : sessionCity.trim();
      const currentYear = new Date().getFullYear().toString();

      const analysis =
        await continuousComplianceService.analyzeDocumentForCompliance({
          file,
          facility_type: facilityType,
          country,
          state,
          city,
          current_year: currentYear,
          task: "survey readiness preparation",
          document_type: documentType,
        });

      setAffableFileAnalysis(analysis);

      // Also persist this analysis into the standard analysis history
      try {
        const userIdStr = getCurrentUserId();
        const userId = userIdStr ? parseInt(userIdStr, 10) : 0;
        const facilityId = currentFacilityData?.facility_id || 0;

        if (userId && facilityId) {
          const historyType = "Policy and Procedure Review";

          await continuousComplianceService.saveAnalysisHistory({
            user_id: userId,
            facility_id: facilityId,
            type: historyType,
            data_obj: {
              analysis: (analysis as any)?.analysis || analysis,
              metadata: {
                ...(analysis as any)?.metadata,
                source: "AffableFLOW",
                original_file_name: file.name,
              },
            },
          });

          // Trigger a re-fetch of the history list so the new entry appears
          setHistoryRefreshCounter((c) => c + 1);
        }
      } catch (historyError) {
        console.error(
          "Failed to save analysis history for AffableFLOW file:",
          historyError,
        );
        // Non-blocking: main Affable analysis flow should still succeed
      }
    } catch (analysisError) {
      console.error("AffableBPM file analysis failed:", analysisError);

      // Check if it's an authentication error
      if (
        analysisError instanceof Error &&
        analysisError.message.includes("Not authenticated")
      ) {
        setErrorAffableFile("Session expired. Please sign in again.");
        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setErrorAffableFile(
          analysisError instanceof Error
            ? analysisError.message
            : "Analysis failed",
        );
      }
    }
  };

  /**
   * Handles uploading a selected AffableBPM file to Policy & Procedure Review
   */
  const handleUploadSelectedAffableFile = async () => {
    if (!selectedAffableFileKey || !selectedAffableFolder) {
      setErrorAffableFile("No file selected");
      return;
    }

    if (!selectedAffableDocument) {
      setErrorAffableFile("No document selected");
      return;
    }

    // Extract folderId and imageId from the selected file key
    const [folderIdStr, imageIdStr] = selectedAffableFileKey.split("-");
    const imageId = Number(imageIdStr);
    const folderId = Number(folderIdStr);

    if (!imageId || isNaN(imageId)) {
      setErrorAffableFile("Invalid file ID");
      return;
    }

    // Find the selected image to get its name and type
    const selectedImage = selectedAffableFolder.images.find(
      (img) => img.id === imageId,
    );

    if (!selectedImage) {
      setErrorAffableFile("Selected file not found");
      return;
    }

    setLoadingAffableFile(true);
    setErrorAffableFile(null);
    setAffableResultUploadSuccess(null);
    setAffableResultUploadError(null);

    try {
      const orgNameFromFacility = (resolvedFacility as any)?.org_name;
      const orgName = (orgNameFromFacility || "").trim();
      if (!orgName) {
        throw new Error(
          "Facility name unavailable. Please select a facility first.",
        );
      }

      const fileResponse: ExternalGetFileByFileIdResponse =
        await affablebpmIntegrationService.getFileByFileId({
          orgName,
          fileId: imageId,
          deptId: 101,
          orgId: 0,
        });

      if (fileResponse.Status !== "Success" || !fileResponse.File) {
        throw new Error(
          fileResponse.Status || "Failed to fetch file from AffableBPM",
        );
      }

      const fileData = fileResponse.File;
      setAffableUploadContext({
        documentId: selectedAffableDocument.id,
        folderId,
        orgName,
        deptId: fileData.DeptID,
        orgId: 0,
      });

      if (!fileData.Base64 || fileData.Base64.trim() === "") {
        throw new Error("File data is empty");
      }

      const imageType = fileData.ImageType || selectedImage.type || ".pdf";
      const mimeTypeMap: Record<string, string> = {
        ".pdf": "application/pdf",
        ".doc": "application/msword",
        ".docx":
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ".html": "text/html",
        ".htm": "text/html",
        ".txt": "text/plain",
        ".xls": "application/vnd.ms-excel",
        ".xlsx":
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      };
      const mimeType =
        mimeTypeMap[imageType.toLowerCase()] || "application/octet-stream";

      let baseName = fileData.ImageName || selectedImage.name;
      if (baseName.toLowerCase().endsWith(imageType.toLowerCase())) {
        baseName = baseName.slice(0, -imageType.length);
      }
      const filename = `${baseName}${imageType}`;

      const file = base64ToFile(fileData.Base64, filename, mimeType);

      await analyzeAffableFileForCompliance(file);
    } catch (error) {
      console.error("Failed to upload AffableBPM file:", error);

      // Check if it's an authentication error
      if (
        error instanceof Error &&
        error.message.includes("Not authenticated")
      ) {
        setErrorAffableFile("Session expired. Please sign in again.");
        // Redirect to login page after a short delay
        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setErrorAffableFile(
          error instanceof Error ? error.message : "Failed to upload file",
        );
      }
    } finally {
      setLoadingAffableFile(false);
    }
  };

  const handleAffableResultUploadButtonClick = async () => {
    setAffableResultUploadError(null);
    setAffableResultUploadSuccess(null);

    if (!affableUploadContext) {
      setAffableResultUploadError(
        "No AffableBPM destination detected. Please analyze a document from AffableBPM first.",
      );
      return;
    }

    if (!affableFileAnalysis) {
      setAffableResultUploadError(
        "No analysis available to upload. Please run the analysis first.",
      );
      return;
    }

    try {
      setIsUploadingAffableResult(true);

      // Derive base name from original Affable file, if available
      const originalName = affableFile?.name || "ComplianceReport";
      const baseName = originalName.replace(/\.[^/.]+$/, "");

      // Build timestamp suffix: YYYYMMDD_HHmmss
      const now = new Date();
      const pad = (n: number) => n.toString().padStart(2, "0");
      const timestamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(
        now.getDate(),
      )}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(
        now.getSeconds(),
      )}`;

      const uploadFileName = `${baseName}_Analysis_${timestamp}.pdf`;

      // Generate the analysis PDF as a Blob (no browser download)
      const pdfBlob = (await generateStyledCompliancePDF(
        affableFileAnalysis,
        "policy",
        { output: "blob" },
      )) as Blob;

      const pdfFile = new File([pdfBlob], uploadFileName, {
        type: "application/pdf",
      });

      // Upload generated PDF to AffableBPM using ExternalFileUpload
      const uploadResult = await affablebpmIntegrationService.uploadFile({
        orgName: affableUploadContext.orgName,
        documentId: affableUploadContext.documentId,
        folderId: affableUploadContext.folderId,
        deptId: affableUploadContext.deptId,
        orgId: 0,
        file: pdfFile,
        fileName: pdfFile.name,
      });

      setAffableResultUploadSuccess(
        "Analysis PDF generated and uploaded to AffableFLOW successfully.",
      );
      toast.success(
        uploadResult?.Message ||
          "Analysis PDF generated and uploaded to AffableFLOW successfully.",
      );

      // Refresh AffableBPM document library so the new PDF appears in the list
      await handleAffableRecordsSync(affableDocsPage - 1); // Convert 1-indexed page to 0-indexed
    } catch (error) {
      console.error("Failed to upload analysis PDF to AffableFLOW:", error);
      const message =
        error instanceof Error
          ? error.message
          : "Failed to upload analysis PDF to AffableFLOW.";
      setAffableResultUploadError(message);
      toast.error(message);
    } finally {
      setIsUploadingAffableResult(false);
    }
  };

  const shouldShowAffablePanel =
    isAffableUiEnabled &&
    !isAffablePanelClosed &&
    (hasAttemptedAffableSync ||
      isSyncingAffableRecords ||
      affableDocuments.length > 0 ||
      Boolean(affableSyncError));

  const renderAffableSyncPanel = () => {
    if (!shouldShowAffablePanel) return null;

    return (
      <Box>
        {/* Back to upload files button - show when not loading and records exist or analysis available */}
        {!isSyncingAffableRecords &&
          (affableRecords.length > 0 || affableFileAnalysis) && (
            <Box
              onClick={() => {
                setIsAffablePanelClosed(true);
                setAffableDocuments([]);
                setAffableRecords([]);
                setSelectedAffableDocumentId(null);
                setSelectedAffableFolderId(null);
                setSelectedAffableFileKey(null);
                setHasAttemptedAffableSync(false);
                setAffableFileAnalysis(null);
                setAffableFile(null);
                setAffableFileUploadDate(null);
                setShowAffableFileReport(false);
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                cursor: "pointer",
                mb: 2,
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              <AffableConnectedIcon />
              <Typography
                sx={{
                  fontFamily: '"Rethink Sans", sans-serif',
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#262626",
                }}
              >
                Back to upload files
              </Typography>
            </Box>
          )}

        {affableSyncError && (
          <Alert
            severity="error"
            onClose={() => {
              setAffableSyncError(null);
              // No folders/files: hide Affable panel so manual upload + Affable
              // actions in the drop zone are visible again.
              const hasNoAffableData =
                !affableDocuments.length &&
                !affableRecords.length &&
                !affableFileAnalysis;
              if (hasNoAffableData) {
                setIsAffablePanelClosed(true);
                setHasAttemptedAffableSync(false);
                setSelectedAffableDocumentId(null);
                setSelectedAffableFolderId(null);
                setSelectedAffableFileKey(null);
              }
            }}
            sx={{ mb: 1 }}
          >
            {affableSyncError}
          </Alert>
        )}

        {/* Show analysis results when available */}
        {affableFileAnalysis && affableFile && (
          <Box>
            {/* File card with View Analysis button */}
            <FileDisplayContainer
              sx={{
                flexDirection: "row",
                width: "calc(100% - 32px)",
                marginBottom: showAffableFileReport ? "12px" : 0,
              }}
            >
              <FileInfoContainer>
                <FileIcon>
                  <ComplianceHistoryFileGlyph />
                </FileIcon>
                <FileDetails sx={{ maxWidth: "250px", minWidth: 0 }}>
                  <FileName
                    sx={{
                      maxWidth: "100%",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={affableFile.name}
                  >
                    {affableFile.name}
                  </FileName>
                  <FileSize>
                    {(affableFile.name.split(".").pop() || "").toLowerCase()} |{" "}
                    {formatFileSize(affableFile.size)}
                    {affableFileUploadDate && (
                      <>
                        {" "}
                        |{" "}
                        <span style={{ textTransform: "none" }}>
                          Uploaded: {affableFileUploadDate}
                        </span>
                      </>
                    )}
                  </FileSize>
                </FileDetails>
              </FileInfoContainer>
              <Box
                sx={{
                  display: "flex",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                <ReUploadButton
                  onClick={() => {
                    setAffableFileAnalysis(null);
                    setAffableFile(null);
                    setAffableFileUploadDate(null);
                    setShowAffableFileReport(false);
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 20 20"
                    fill="none"
                  >
                    <path
                      d="M13.8891 3.58537C13.2006 2.68731 12.1509 2.11111 10.9761 2.11111C9.19946 2.11111 7.65885 3.2022 7.15885 4.77778H6.66663C4.8257 4.77778 3.33329 6.27019 3.33329 8.11111C3.33329 9.95204 4.8257 11.4444 6.66663 11.4444H7.77774V10.2222H6.66663C5.50108 10.2222 4.55552 9.27667 4.55552 8.11111C4.55552 6.94556 5.50108 6 6.66663 6H7.79885L8.02885 5.24444C8.39329 4.02 9.58329 3.33333 10.9761 3.33333C11.7983 3.33333 12.5594 3.70556 13.0483 4.34L13.5206 4.94889L14.1039 4.44222C15.1428 3.61778 16.6 3.83111 17.3872 4.88889C18.1772 5.95111 17.9428 7.40667 16.8728 8.18L15.5555 9.15556V10.7772L17.4633 9.34C19.2372 8.02889 19.6328 5.52 18.3217 3.74667C17.0106 1.97333 14.5017 1.57556 12.7278 2.88667L12.5261 3.04L13.1094 3.54667L12.6372 4.15556L13.8891 3.58537Z"
                      fill="#065B70"
                    />
                    <path
                      d="M13.3333 10V17.7778L15.5 15.6111L16.3889 16.5L12.6667 20.2222L8.94446 16.5L9.83335 15.6111L12 17.7778V10H13.3333Z"
                      fill="#065B70"
                    />
                  </svg>
                  Re-Upload
                </ReUploadButton>
                <DeleteButton
                  aria-label="Remove Affable uploaded file"
                  onClick={() => {
                    setAffableFileAnalysis(null);
                    setAffableFile(null);
                    setAffableFileUploadDate(null);
                    setShowAffableFileReport(false);
                  }}
                >
                  <DeleteFileIcon />
                </DeleteButton>
                <GlobalAdminLlmPromptButton
                  dialogTitle="Policy & procedure compliance — LLM prompt"
                  promptText={
                    affableFileAnalysis?.llmPromptsBySection?.policy_procedure
                  }
                  wallTimeSec={
                    affableFileAnalysis?.llmInvokeWallSecondsBySection
                      ?.policy_procedure ?? null
                  }
                />
                <ViewReportButton
                  onClick={() =>
                    setShowAffableFileReport(!showAffableFileReport)
                  }
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                  >
                    <path
                      d="M3.5 3C3.5 1.89543 4.39543 1 5.5 1H9.08579C9.48361 1 9.86514 1.15804 10.1464 1.43934L13.0607 4.35355C13.342 4.63486 13.5 5.01639 13.5 5.41421V13C13.5 14.1046 12.6046 15 11.5 15H5.5C4.39543 15 3.5 14.1046 3.5 13V3ZM5.5 2C4.94772 2 4.5 2.44772 4.5 3V13C4.5 13.5523 4.94772 14 5.5 14H11.5C12.0523 14 12.5 13.5523 12.5 13V6H10C9.17157 6 8.5 5.32843 8.5 4.5V2H5.5ZM10 5H12.2929L9.5 2.20711V4.5C9.5 4.77614 9.72386 5 10 5Z"
                      fill="white"
                    />
                  </svg>
                  {showAffableFileReport ? "Hide Report" : "View Analysis"}
                </ViewReportButton>
              </Box>
            </FileDisplayContainer>

            {/* Display the analysis report only when View Analysis is clicked */}
            {showAffableFileReport && (
              <Box>
                {renderReportDetails(affableFileAnalysis, "policy", {
                  enableAffableUploadButton: !!affableUploadContext,
                })}
              </Box>
            )}
          </Box>
        )}

        {/* Show 3 boxes only when no analysis results */}
        {!affableFileAnalysis &&
          affableRecords.length > 0 &&
          (loadingAffableFile ? (
            <Box
              sx={{
                textAlign: "center",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 1,
                padding: "32px",
              }}
            >
              <CircularProgress size={24} sx={{ color: "#065b70" }} />
              <Typography
                sx={{
                  fontSize: "17px",
                  color: "#065b70",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                Analyzing document…
              </Typography>
              <Typography
                sx={{
                  fontSize: "15px",
                  color: "#737373",
                  fontFamily: "Rethink Sans, sans-serif",
                }}
              >
                This AI-assisted analysis may take a few minutes.
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "stretch",
                mt: 2,
                mb: 2,
              }}
            >
            <Box
              sx={{
                width: "45%",
                minWidth: 0,
                border: "1px solid #0a829d",
                borderRadius: "4px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Table Header */}
              <Box
                sx={{
                  backgroundColor: "#0a829d",
                  display: "flex",
                  alignItems: "center",
                  p: 1,
                  flexShrink: 0,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Rethink Sans", sans-serif',
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#ffffff",
                  }}
                >
                  Policy Name
                </Typography>
              </Box>

              {/* Table Body - Dynamic */}
              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  maxHeight: "280px",
                  "&::-webkit-scrollbar": {
                    width: "4px",
                  },
                  "&::-webkit-scrollbar-track": {
                    backgroundColor: "#f1f1f1",
                    borderRadius: "2px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "#0a829d",
                    borderRadius: "2px",
                    "&:hover": {
                      backgroundColor: "#086e86",
                    },
                  },
                }}
              >
                {affableRecords.map((record) => {
                  const isSelected =
                    selectedAffableDocumentId === record.DocumentID;
                  // Get the first column value as the policy name
                  const policyName =
                    affableTableColumns.length > 0
                      ? record[affableTableColumns[0]] || "Untitled Policy"
                      : "Untitled Policy";
                  // Find the matching document to get the file count
                  const matchingDoc = affableDocuments.find(
                    (doc) => doc.id === record.DocumentID,
                  );
                  const fileCount = matchingDoc
                    ? matchingDoc.folders.reduce(
                        (total, folder) => total + folder.images.length,
                        0,
                      )
                    : 0;
                  return (
                    <Box
                      key={record.DocumentID}
                      onClick={() => {
                        setSelectedAffableDocumentId(record.DocumentID);
                      }}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px 12px",
                        borderBottom: "1px solid #e5f5f8",
                        gap: "8px",
                        cursor: "pointer",
                        backgroundColor: isSelected ? "#e6fafc" : "#ffffff",
                        "&:hover": {
                          backgroundColor: isSelected ? "#d4edf3" : "#f5feff",
                        },
                        "&:last-child": {
                          borderBottom: "none",
                        },
                      }}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                      >
                        <path
                          d="M4 2C3.44772 2 3 2.44772 3 3V13C3 13.5523 3.44772 14 4 14H12C12.5523 14 13 13.5523 13 13V5.41421C13 5.149 12.8946 4.89464 12.7071 4.70711L10.2929 2.29289C10.1054 2.10536 9.851 2 9.58579 2H4ZM5 5H8V6H5V5ZM5 8H11V9H5V8ZM5 11H11V12H5V11Z"
                          fill="#737373"
                        />
                      </svg>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "13px",
                          color: "#262626",
                          flex: 1,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          fontWeight: isSelected ? 600 : 400,
                        }}
                      >
                        {policyName}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: '"Rethink Sans", sans-serif',
                          fontSize: "12px",
                          color: "#737373",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {fileCount} {fileCount === 1 ? "file" : "files"}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Pagination Row - Fixed at bottom */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  padding: "4px 8px",
                  borderTop: "1px solid #e5f5f8",
                  gap: "4px",
                  flexShrink: 0,
                  backgroundColor: "#ffffff",
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flexWrap: "wrap",
                    py: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      ml: "auto",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"Rethink Sans", sans-serif',
                        fontSize: 12,
                        color: "#065b70",
                      }}
                    >
                      Rows per page
                    </Typography>
                    <Select
                      size="small"
                      value={affableRowsPerPage}
                      onChange={(e) => {
                        const newPageSize = Number(e.target.value);
                        setAffableRowsPerPage(newPageSize);
                        setAffableDocsPage(1);
                        handleAffableRecordsSync(0, newPageSize);
                      }}
                      sx={{
                        minWidth: 50,
                        width: 50,
                        height: 28,
                        "& .MuiSelect-select": {
                          padding: "4px 8px",
                          fontSize: 12,
                          display: "flex",
                          alignItems: "center",
                        },
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            "& .MuiMenuItem-root": {
                              fontSize: 12,
                              minHeight: "28px",
                              lineHeight: "28px",
                              fontFamily: '"Rethink Sans", sans-serif',
                            },
                          },
                        },
                      }}
                    >
                      <MenuItem value={5}>5</MenuItem>
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                    </Select>
                    <Pagination
                      count={totalAffableDocPages}
                      page={affableDocsPage}
                      onChange={(_, value) => {
                        setAffableDocsPage(value);
                        handleAffableRecordsSync(value - 1);
                      }}
                      color="primary"
                      size="small"
                      sx={{
                        "& .MuiPaginationItem-root": {
                          fontSize: 12,
                          height: 28,
                          minWidth: 28,
                          padding: "0 6px",
                        },
                        "& .MuiPaginationItem-root.Mui-selected": {
                          backgroundColor: "#0a829d",
                          color: "#ffffff",
                        },
                        "& .MuiPaginationItem-root.Mui-selected:hover": {
                          backgroundColor: "#0a829d",
                        },
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>

            {affableDocuments.length > 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  width: "55%",
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    gap: 0,
                    alignItems: "stretch",
                  }}
                >
                  <Box
                    sx={{
                      width: "35%",
                      borderRadius: "4px 0 0 4px",
                      border: "1px solid #0a829d",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      height: "240px",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#0a829d",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#ffffff",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {(() => {
                          const selectedRecord = affableRecords.find(
                            (r) => r.DocumentID === selectedAffableDocumentId,
                          );
                          if (
                            selectedRecord &&
                            affableTableColumns.length > 0
                          ) {
                            return (
                              selectedRecord[affableTableColumns[0]] ||
                              "Documents"
                            );
                          }
                          return "Documents";
                        })()}
                      </Typography>
                    </Box>

                    <Box sx={{ maxHeight: 260, overflowY: "auto" }}>
                      {selectedAffableDocument?.folders.length ? (
                        selectedAffableDocument.folders.map((folder) => {
                          const isActive =
                            selectedAffableFolder &&
                            selectedAffableFolder.id === folder.id;
                          return (
                            <Box
                              key={folder.id}
                              sx={{
                                px: 1.5,
                                py: 1,
                                cursor: "pointer",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                gap: 1,
                                backgroundColor: isActive
                                  ? "#e6fafc"
                                  : "transparent",
                                "&:hover": {
                                  backgroundColor: "#f5feff",
                                },
                              }}
                              onClick={() =>
                                setSelectedAffableFolderId(folder.id)
                              }
                            >
                              <Typography
                                sx={{
                                  fontSize: "14px",
                                  color: "#043c4a",
                                  fontWeight: isActive ? 600 : 500,
                                }}
                              >
                                {folder.name}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#0a829d",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {folder.images.length}{" "}
                                {folder.images.length === 1 ? "file" : "files"}
                              </Typography>
                            </Box>
                          );
                        })
                      ) : (
                        <Typography
                          sx={{ fontSize: "13px", color: "#4f6f78", p: 1.5 }}
                        >
                          No folders available for this document yet.
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      width: "65%",
                      borderRadius: "0 4px 4px 0",
                      border: "1px solid #0a829d",
                      borderLeft: "none",
                      backgroundColor: "#ffffff",
                      overflow: "hidden",
                      height: "240px",
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#ffffff",
                        borderBottom: "1px solid #0a829d",
                        p: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: "#043c4a",
                        }}
                      >
                        {selectedAffableFolder?.name || "Default"}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 2, flex: 1, overflow: "hidden" }}>
                      {!selectedAffableFolder ||
                      !selectedAffableFolder.images.length ? (
                        <Typography sx={{ fontSize: "14px", color: "#4f6f78" }}>
                          No files available in this folder.
                        </Typography>
                      ) : (
                        <>
                          <Box
                            sx={{
                              maxHeight: "100%",
                              height: "100%",
                              overflowY: "auto",
                              overflowX: "auto",
                              pr: 1,
                              "&::-webkit-scrollbar": {
                                width: "4px",
                                height: "4px",
                              },
                              "&::-webkit-scrollbar-track": {
                                backgroundColor: "#f1f1f1",
                                borderRadius: "2px",
                              },
                              "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#0a829d",
                                borderRadius: "2px",
                                "&:hover": {
                                  backgroundColor: "#086e86",
                                },
                              },
                            }}
                          >
                            {selectedAffableFolder.images.map((image) => (
                              <Box
                                key={`${selectedAffableFolder.id}-${image.id}`}
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 1,
                                }}
                              >
                                <Checkbox
                                  size="small"
                                  checked={isAffableFileSelected(
                                    selectedAffableFolder.id,
                                    image.id,
                                  )}
                                  onChange={() =>
                                    selectAffableFile(
                                      selectedAffableFolder.id,
                                      image.id,
                                    )
                                  }
                                />
                                <AffableFileRow>
                                  <AffableFileName>
                                    {image.name}
                                    {image.type ? ` (${image.type})` : ""}
                                  </AffableFileName>
                                  <AffableFileMeta>
                                    {image.createdAt ||
                                      "No created date provided"}
                                  </AffableFileMeta>
                                </AffableFileRow>
                              </Box>
                            ))}
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
                {/* Analyse Selected File Button */}
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    padding: "5px 5px",
                    mt: 1,
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    variant="contained"
                    onClick={handleUploadSelectedAffableFile}
                    disabled={loadingAffableFile || !selectedAffableFileKey}
                    sx={{
                      backgroundColor: "#0a829d",
                      color: "#ffffff",
                      borderRadius: "8px",
                      padding: "8px 12px",
                      fontSize: "14px",
                      fontWeight: 600,
                      fontFamily: '"Rethink Sans", sans-serif',
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "#086e86",
                      },
                      "&:disabled": {
                        backgroundColor: "#b0d4dc",
                        color: "#ffffff",
                      },
                    }}
                  >
                    {loadingAffableFile ? (
                      <>
                        <CircularProgress
                          size={16}
                          sx={{ color: "#ffffff", mr: 1 }}
                        />
                        Analysing...
                      </>
                    ) : (
                      "Analyze Selected File"
                    )}
                  </Button>
                </Box>
              </Box>
            ) : (
              !isSyncingAffableRecords &&
              !affableSyncError && (
                <Typography sx={{ fontSize: "14px", color: "#4f6f78" }}>
                  No documents available for this facility yet.
                </Typography>
              )
            )}
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", gap: 0, width: "100%" }}
    >
      <ComplianceHeader>
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "8px", minWidth: 0 }}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="21"
            viewBox="0 0 20 21"
            fill="none"
          >
            <path
              d="M8.5 6.5V15.5H11.5V6.5C11.5 5.94772 11.0523 5.5 10.5 5.5H9.5C8.94772 5.5 8.5 5.94772 8.5 6.5ZM7.5 9.5V6.5C7.5 5.39543 8.39543 4.5 9.5 4.5H10.5C11.6046 4.5 12.5 5.39543 12.5 6.5V7.5H14C15.1046 7.5 16 8.39543 16 9.5V16C16 16.2761 15.7761 16.5 15.5 16.5H4.5C4.22386 16.5 4 16.2761 4 16V11.5C4 10.3954 4.89543 9.5 6 9.5H7.5ZM7.5 15.5V10.5H6C5.44772 10.5 5 10.9477 5 11.5V15.5H7.5ZM12.5 15.5H15V9.5C15 8.94772 14.5523 8.5 14 8.5H12.5V15.5Z"
              fill="#065B70"
            />
          </svg>
          <Typography
            sx={{
              color: "var(--Brand-Colors-color-brand-Pressed, #065B70)",
              fontSize: "16px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
            }}
          >
            Continuous Compliance
          </Typography>
        </Box>
        {showWatchGuide && (
          <Button
          variant="outlined"
          disabled={!onWatchGuideClick || watchGuideLoading}
          onClick={onWatchGuideClick}
          sx={{
            display: "flex",
            flexShrink: 0,
            minHeight: "40px",
            padding: "8px 16px",
            justifyContent: "center",
            alignItems: "center",
            gap: "8px",
            borderRadius: "8px",
            border: "1px solid #5BBFD6",
            background: "#FFFFFF",
            boxShadow: "0 2px 2px 0 rgba(42, 116, 131, 0.20)",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#F8FDFF",
              borderColor: "#3CA9C2",
            },
          }}
        >
          {watchGuideIconSrc ? (
            <Box
              component="img"
              src={watchGuideIconSrc}
              alt="Watch guide"
              sx={{ width: 16, height: 16, display: "block" }}
            />
          ) : null}
          <Typography
            sx={{
              color: "#086E86",
              fontFamily: '"Rethink Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              lineHeight: "normal",
            }}
          >
            {watchGuideLoading ? "Loading Guide..." : "Watch Guide"}
          </Typography>
          </Button>
        )}
      </ComplianceHeader>
      {/* <Typography
        sx={{
          color: "#525252",
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
          ml: "28px",
        }}
      >
        AI-assisted analysis. Human review and verification required.
      </Typography> */}

      <ContinuousComplianceContainer sx={{ mt: "12px" }}>
        <AccordionsContainer>
          {renderAffableFileAnalysisAccordion()}
          {renderUploadAccordion("Policy & Procedure Review")}
          {/* SSOT-76 / #10192: emergency/survey Continuous Compliance placeholders hidden until built */}
          {renderRecentlyReviewedDocuments()}
        </AccordionsContainer>
      </ContinuousComplianceContainer>
    </Box>
  );
}
